import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import SmsConsentField from "../components/SmsConsentField";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { useLocale } from "../context/LocaleContext";
import { useSEO } from "../hooks/useSEO";
import { getRecaptchaToken } from "../utils/recaptcha";
import heroBanner from "../assets/WhatsApp Image 2026-03-20 at 2.51.26 PM (1).jpeg";

const BACKEND_BASE = (() => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isLocal = host === "localhost" || host === "127.0.0.1";
    if (!isLocal) {
      return window.location.origin;
    }
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || "";
})();

const API = `${BACKEND_BASE}/api`;
const getErr = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return "Error submitting request";
};

const COUNTRIES = [
  { code: "+1",    flag: "🇺🇸", name: "United States", iso: "US" },
  { code: "+1",    flag: "🇨🇦", name: "Canada",         iso: "CA" },
  { code: "+52",   flag: "🇲🇽", name: "México",         iso: "MX" },
  { code: "+44",   flag: "🇬🇧", name: "United Kingdom", iso: "GB" },
  { code: "+34",   flag: "🇪🇸", name: "España",         iso: "ES" },
  { code: "+54",   flag: "🇦🇷", name: "Argentina",      iso: "AR" },
  { code: "+57",   flag: "🇨🇴", name: "Colombia",       iso: "CO" },
  { code: "+56",   flag: "🇨🇱", name: "Chile",          iso: "CL" },
  { code: "+51",   flag: "🇵🇪", name: "Perú",           iso: "PE" },
  { code: "+58",   flag: "🇻🇪", name: "Venezuela",      iso: "VE" },
  { code: "+593",  flag: "🇪🇨", name: "Ecuador",        iso: "EC" },
  { code: "+506",  flag: "🇨🇷", name: "Costa Rica",     iso: "CR" },
  { code: "+507",  flag: "🇵🇦", name: "Panamá",         iso: "PA" },
  { code: "+1787", flag: "🇵🇷", name: "Puerto Rico",    iso: "PR" },
  { code: "+55",   flag: "🇧🇷", name: "Brasil",         iso: "BR" },
  { code: "+598",  flag: "🇺🇾", name: "Uruguay",        iso: "UY" },
  { code: "+33",   flag: "🇫🇷", name: "France",         iso: "FR" },
  { code: "+49",   flag: "🇩🇪", name: "Germany",        iso: "DE" },
  { code: "+39",   flag: "🇮🇹", name: "Italy",          iso: "IT" },
  { code: "+61",   flag: "🇦🇺", name: "Australia",      iso: "AU" },
];

const STAGES = [
  { icon: "👤", en: "Contact",  es: "Contacto",  subEN: "Who are you?",        subES: "¿Quién eres?" },
  { icon: "📍", en: "Address",  es: "Dirección", subEN: "Where to pick up?",   subES: "¿Dónde recogemos?" },
  { icon: "🧺", en: "Service",  es: "Servicio",  subEN: "What do you need?",   subES: "¿Qué necesitas?" },
  { icon: "📅", en: "Schedule", es: "Horario",   subEN: "When works for you?", subES: "¿Cuándo?" },
  { icon: "✅", en: "Confirm",  es: "Confirmar", subEN: "Review & launch",     subES: "Revisar y enviar" },
];

const WASH_PHASES = ["Pre-wash", "Washing", "Rinse", "Spin", "Done!"];

const RECURRENCE_OPTIONS = [
  { val: "once",       icon: "1️⃣", en: "One time",       es: "Una sola vez" },
  { val: "twice_week", icon: "🔄", en: "Twice a week",    es: "Dos veces por semana" },
  { val: "weekly",     icon: "📅", en: "Every week",      es: "Cada semana" },
  { val: "biweekly",   icon: "📆", en: "Every 2 weeks",   es: "Cada 2 semanas" },
];

const RECURRENCE_LABELS = {
  once:       { en: "One time",      es: "Una sola vez" },
  weekly:     { en: "Every week",    es: "Cada semana" },
  biweekly:   { en: "Every 2 weeks", es: "Cada 2 semanas" },
  twice_week: { en: "Twice a week",  es: "Dos veces por semana" },
};

const WEEKDAYS = [
  { value: "Monday",    label: { en: "Monday",    es: "Lunes" } },
  { value: "Tuesday",   label: { en: "Tuesday",   es: "Martes" } },
  { value: "Wednesday", label: { en: "Wednesday", es: "Miércoles" } },
  { value: "Thursday",  label: { en: "Thursday",  es: "Jueves" } },
  { value: "Friday",    label: { en: "Friday",    es: "Viernes" } },
  { value: "Saturday",  label: { en: "Saturday",  es: "Sábado" } },
  { value: "Sunday",    label: { en: "Sunday",    es: "Domingo" } },
];

const WeekdaySelector = ({ selectedDays, onChange, locale, t }) => {
  const toggleDay = (dayValue) => {
    const newSelected = selectedDays.includes(dayValue)
      ? selectedDays.filter(d => d !== dayValue)
      : [...selectedDays, dayValue];
    onChange(newSelected);
  };
  const warning = selectedDays.length > 2
    ? t("You can select up to 2 days", "Puedes seleccionar hasta 2 días")
    : null;

  return (
    <div style={{ marginTop: 12, animation: "tl_panel .2s ease both" }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".13em", color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>
        {t("Select two days for pickup", "Selecciona dos días para la recogida")} *
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        {WEEKDAYS.map(day => {
          const active = selectedDays.includes(day.value);
          return (
            <button key={day.value} type="button" onClick={() => toggleDay(day.value)}
              style={{ padding: "6px 12px", borderRadius: 20,
                border: `1.5px solid ${active ? "#0ea5e9" : "hsl(var(--border))"}`,
                background: active ? "rgba(14,165,233,.1)" : "hsl(var(--secondary))",
                cursor: "pointer", fontSize: 11, fontWeight: active ? 700 : 500,
                color: active ? "#0ea5e9" : "hsl(var(--foreground))", transition: "all .15s" }}>
              {locale === "es" ? day.label.es : day.label.en}
            </button>
          );
        })}
      </div>
      {warning && <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>{warning}</div>}
      {selectedDays.length > 0 && selectedDays.length < 2 && (
        <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 2 }}>
          {t("Please select exactly two days", "Por favor selecciona exactamente dos días")}
        </div>
      )}
    </div>
  );
};

// ─── Address Validation Status Component ─────────────────────────────────────
// FIX: este componente ahora usa el `deliveryFee` REAL que llega del backend
// (calculado por delivery_config.py / calculate_shipping_distance en el paso 1)
// en lugar de recalcularlo con una fórmula local aproximada. Antes existían
// dos fuentes de verdad para el mismo dato y el usuario podía ver un monto
// distinto al que realmente se le cobraría.
const AddressValidationStatus = ({ checking, valid, error, distanceMiles, deliveryFee, locale, t }) => {
  if (!checking && !valid && !error) return null;

  if (checking) {
    return (
      <div style={{
        marginTop: 10,
        padding: "10px 14px",
        borderRadius: 10,
        background: "rgba(14,165,233,.06)",
        border: "1.5px solid rgba(14,165,233,.25)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        animation: "tl_panel .2s ease both",
      }}>
        <div style={{
          width: 18, height: 18, borderRadius: "50%",
          border: "2.5px solid rgba(14,165,233,.2)",
          borderTopColor: "#0ea5e9",
          animation: "tl_spin .7s linear infinite",
          flexShrink: 0,
        }} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0ea5e9" }}>
            {t("Verifying address…", "Verificando dirección…")}
          </div>
          <div style={{ fontSize: 10, color: "rgba(14,165,233,.7)", marginTop: 1 }}>
            {t("Checking if we cover your area", "Verificando si cubrimos tu área")}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        marginTop: 10,
        padding: "10px 14px",
        borderRadius: 10,
        background: "rgba(220,38,38,.05)",
        border: "1.5px solid rgba(220,38,38,.3)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        animation: "tl_panel .2s ease both",
      }}>
        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>❌</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>
            {t("Address outside service area", "Dirección fuera del área de servicio")}
          </div>
          <div style={{ fontSize: 10, color: "#b91c1c", marginTop: 2, lineHeight: 1.5 }}>
            {error}
          </div>
          <div style={{ fontSize: 10, color: "#b91c1c", marginTop: 4 }}>
            {t("We currently serve Ventura, Oxnard and surrounding areas.",
               "Actualmente servimos Ventura, Oxnard y áreas cercanas.")}
          </div>
        </div>
      </div>
    );
  }

  if (valid) {
    // FIX: ya no se recalcula el fee localmente. Se usa directamente el valor
    // que vino del backend (prop `deliveryFee`). `isFree` se deriva de ese
    // mismo valor real, no de una fórmula aparte basada solo en millas.
    const hasFeeValue = deliveryFee !== null && deliveryFee !== undefined;
    const isFree = hasFeeValue && parseFloat(deliveryFee) === 0;
    const deliveryFeeLabel = hasFeeValue
      ? (isFree ? t("FREE", "GRATIS") : `$${parseFloat(deliveryFee).toFixed(2)}`)
      : null;

    return (
      <div style={{
        marginTop: 10,
        padding: "10px 14px",
        borderRadius: 10,
        background: "rgba(5,150,105,.05)",
        border: "1.5px solid rgba(5,150,105,.3)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        animation: "tl_panel .2s ease both",
      }}>
        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>✅</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#059669" }}>
            {t("Address verified — we cover your area!", "¡Dirección verificada — cubrimos tu área!")}
          </div>
          {(distanceMiles || deliveryFeeLabel) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
              {distanceMiles && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 9px", borderRadius: 20,
                  background: "rgba(5,150,105,.1)", border: "1px solid rgba(5,150,105,.25)",
                  fontSize: 11, fontWeight: 600, color: "#059669",
                }}>
                  📍 {parseFloat(distanceMiles).toFixed(1)} mi {t("from store", "de la tienda")}
                </span>
              )}
              {deliveryFeeLabel && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 9px", borderRadius: 20,
                  background: isFree ? "rgba(5,150,105,.1)" : "rgba(14,165,233,.08)",
                  border: `1px solid ${isFree ? "rgba(5,150,105,.25)" : "rgba(14,165,233,.2)"}`,
                  fontSize: 11, fontWeight: 700,
                  color: isFree ? "#059669" : "#0ea5e9",
                }}>
                  🚚 {t("Delivery", "Entrega")}: {deliveryFeeLabel}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

// ─── Phone input ───────────────────────────────────────────────────────────────
const PhoneInput = ({ value, dialCode, dialIso, onValueChange, onDialCodeChange }) => {
  const [open, setOpen] = useState(false);
  const [srch, setSrch] = useState("");
  const [foc,  setFoc]  = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const cur  = COUNTRIES.find((c) => c.code === dialCode && c.iso === dialIso) || COUNTRIES[0];
  const list = COUNTRIES.filter((c) => c.name.toLowerCase().includes(srch.toLowerCase()) || c.code.includes(srch));
  const fmt  = (raw) => {
    let d = raw.replace(/\D/g, "");
    // FIX: si el autofill del navegador pega el código de país pegado al
    // número (ej. "14845088966", 11 dígitos empezando en 1), un simple
    // .slice(0,10) cortaba los primeros 10 dígitos y descartaba el último
    // dígito REAL del número en vez de quitar el "1" inicial. Ahora, si hay
    // 11 dígitos y el primero es "1", se quita ese primer dígito antes de
    // recortar a 10.
    if (d.length === 11 && d[0] === "1") d = d.slice(1);
    d = d.slice(0, 10);
    if (d.length >= 7) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    if (d.length >= 4) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    if (d.length >= 1) return `(${d}`;
    return "";
  };

  // FIX: cuando Chrome/otros navegadores autocompletan este campo, en varios
  // casos NO disparan el evento 'input' nativo que React usa para su
  // onChange sintético — el navegador escribe el valor directo en el nodo
  // del DOM. React, al no enterarse, sigue pensando que el campo tiene su
  // valor controlado anterior (ej. "") y en el siguiente render lo vuelve a
  // escribir encima, lo que se ve como si el número se fuera "borrando de a
  // dos" según React reconcilia. Truco estándar para detectar el autofill:
  // el navegador aplica la pseudo-clase :-webkit-autofill al campo, y le
  // enganchamos una animación CSS de 0ms (ver <style> más abajo) — cuando
  // esa animación arranca, leemos el valor REAL del DOM y lo sincronizamos
  // a mano con React.
  const handleAutofillDetected = (e) => {
    if (e.animationName !== "onAutoFillStart") return;
    const domValue = inputRef.current?.value || "";
    if (domValue) onValueChange(fmt(domValue));
  };

  const wrap = {
    display: "flex", borderRadius: 12, overflow: "visible",
    border: `1.5px solid ${foc || open ? "#0ea5e9" : "hsl(var(--border))"}`,
    background: "hsl(var(--background))",
    boxShadow: foc || open ? "0 0 0 3px rgba(14,165,233,.12)" : "none",
    transition: "all .15s",
  };

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <div style={wrap}>
        <button type="button" onClick={() => { setOpen((o) => !o); setSrch(""); }}
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "10px 10px 10px 12px",
            background: "rgba(14,165,233,.07)", border: "none", borderRight: "1.5px solid hsl(var(--border))",
            borderRadius: "10px 0 0 10px", cursor: "pointer", color: "#0ea5e9", fontWeight: 700,
            fontSize: 12, fontFamily: "inherit", flexShrink: 0, minWidth: 84 }}>
          <span style={{ fontSize: 17 }}>{cur.flag}</span>
          <span>{cur.code}</span>
          <ChevronDown size={12} style={{ opacity: .6, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
        </button>
        <input type="tel" ref={inputRef} value={value} className="tl-phone-autofill-input"
          onChange={(e) => onValueChange(dialCode === "+1" ? fmt(e.target.value) : e.target.value.replace(/[^\d\s\-()+]/g, ""))}
          onAnimationStart={handleAutofillDetected}
          placeholder={dialCode === "+1" ? "(___) ___-____" : "Phone number"}
          onFocus={() => setFoc(true)} onBlur={() => setFoc(false)}
          style={{ flex: 1, padding: "10px 12px", border: "none", outline: "none", background: "transparent",
            color: "hsl(var(--foreground))", fontSize: 13, fontWeight: 500, fontFamily: "inherit" }} />
      </div>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 5px)", left: 0, width: 260,
          background: "hsl(var(--card))", border: "1.5px solid hsl(var(--border))",
          borderRadius: 12, zIndex: 999, boxShadow: "var(--shadow-lg)", overflow: "hidden", maxHeight: 260 }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))" }}>
            <input type="text" value={srch} onChange={(e) => setSrch(e.target.value)} placeholder="Search country…" autoFocus
              style={{ width: "100%", padding: "6px 10px", border: "1px solid hsl(var(--border))", borderRadius: 7,
                background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12, fontFamily: "inherit", outline: "none" }} />
          </div>
          <div style={{ overflowY: "auto", maxHeight: 200 }}>
            {list.map((c, i) => (
              <button key={`${c.iso}-${i}`} type="button"
                onClick={() => { onDialCodeChange(c.code, c.iso); onValueChange(""); setOpen(false); setSrch(""); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "8px 13px",
                  border: "none", background: "none", cursor: "pointer", color: "hsl(var(--foreground))",
                  fontFamily: "inherit", fontSize: 12, textAlign: "left" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "hsl(var(--secondary))")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "none")}>
                <span style={{ fontSize: 16 }}>{c.flag}</span>
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: "#0ea5e9", fontWeight: 700 }}>{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Washing Machine SVGs ──────────────────────────────────────────────────────
const RiderMachine = ({ step }) => {
  const holes    = [0, 45, 90, 135, 180, 225, 270, 315];
  const spinDurs = ["2.5s", "2s", "1.2s", "0.7s", "0.5s"];
  const spinDur  = spinDurs[step] || "2s";
  const wLevels  = [82, 76, 70, 64, 58];
  const wY       = wLevels[step] || 82;

  return (
    <svg viewBox="0 0 100 130" width="52" style={{ display: "block", overflow: "visible" }}>
      <defs>
        <clipPath id="rc"><circle cx="50" cy="65" r="28" /></clipPath>
        <filter id="rg"><feGaussianBlur stdDeviation="1.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <ellipse cx="50" cy="127" rx="32" ry="4" fill="#000" opacity=".2" />
      <rect x="4" y="16" width="92" height="100" rx="10" fill="#1a3558" stroke="#2a4568" strokeWidth="1" />
      <rect x="4" y="104" width="92" height="12" fill="#0f1e2e" />
      <rect x="4" y="110" width="92" height="6" rx="6" fill="#0f1e2e" />
      <rect x="4" y="4" width="92" height="14" rx="7" fill="#0c1825" stroke="#1e3355" strokeWidth=".6" />
      {[16, 25, 34, 43, 52].map((x, i) => (
        <circle key={i} cx={x} cy="11" r={i === step ? 2.5 : 1.8}
          fill={i <= step ? "#38bdf8" : "#1a3050"}
          opacity={i === step ? 1 : i < step ? 0.9 : 0.35}>
          {i === step && <animate attributeName="r" values="2.5;3.5;2.5" dur="1s" repeatCount="indefinite" />}
        </circle>
      ))}
      <circle cx="82" cy="11" r="5" fill="#0c2d45" stroke="#38bdf8" strokeWidth=".8" filter="url(#rg)" />
      <circle cx="82" cy="11" r="2" fill="#38bdf8" opacity=".9">
        <animate attributeName="opacity" values=".9;.3;.9" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="50" cy="65" r="32" fill="#0b1a2a" stroke="#1a3050" strokeWidth=".8" />
      <circle cx="50" cy="65" r="28" fill="#0e1e2e" />
      <g clipPath="url(#rc)">
        <rect x="22" y={wY} width="56" height={130 - wY} fill="#0ea5e9" opacity=".4">
          <animate attributeName="y" values={`${wY};${wY - 3};${wY}`} dur="1.8s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" />
        </rect>
        {step >= 2 && [{ cx: 36, r: 3 }, { cx: 44, r: 2 }, { cx: 52, r: 3.5 }, { cx: 61, r: 2.5 }].map((b, i) => (
          <circle key={i} cx={b.cx} cy={wY - 1} r={b.r} fill="white" opacity=".5">
            <animate attributeName="cy" values={`${wY-1};${wY-3};${wY-1}`} dur={`${1.6 + i * 0.2}s`} begin={`${i * 0.2}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {step >= 1 && [{ cx: 36 }, { cx: 50 }, { cx: 64 }].map((b, i) => (
          <circle key={i} cx={b.cx} r={2.5 - i * 0.3} fill="#7dd3fc" opacity=".6">
            <animate attributeName="cy" values={`${wY + 5};${wY - 30};${wY + 5}`} dur={`${2 + i * 0.3}s`} begin={`${i * 0.5}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values=".6;0;.6" dur={`${2 + i * 0.3}s`} begin={`${i * 0.5}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <g style={{ transformOrigin: "50px 65px", animation: `tl_spin ${spinDur} linear infinite` }}>
          {holes.map((deg, i) => {
            const rad = (deg * Math.PI) / 180;
            return <circle key={i} cx={50 + 19 * Math.sin(rad)} cy={65 - 19 * Math.cos(rad)} r="3" fill="#071320" stroke="#1e3558" strokeWidth=".5" opacity=".9" />;
          })}
        </g>
        <g transform="translate(50,65)" style={{ transformOrigin: "0 0", animation: `tl_spinr ${spinDur} linear infinite` }}>
          <circle r="8" fill="#050e1a" stroke="#38bdf8" strokeWidth="1" filter="url(#rg)" opacity=".95" />
          <text textAnchor="middle" dy=".35em" fill="#38bdf8" fontSize="4" fontWeight="800" fontFamily="'Manrope',sans-serif" letterSpacing=".8">VFL</text>
        </g>
      </g>
      <circle cx="50" cy="65" r="28" fill="none" stroke="#1e3558" strokeWidth="1.5" />
      <rect x="16" y="115" width="14" height="4" rx="2" fill="#0c1825" />
      <rect x="70" y="115" width="14" height="4" rx="2" fill="#0c1825" />
    </svg>
  );
};

const WashMachine = ({ phase, done }) => {
  const holes = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <svg viewBox="0 0 240 310" width="100%" style={{ maxWidth: 180, display: "block" }}>
      <defs>
        <radialGradient id="wm_b" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1a3558" /><stop offset="50%" stopColor="#243f6a" /><stop offset="100%" stopColor="#1a3558" />
        </radialGradient>
        <clipPath id="wm_c"><circle cx="119" cy="155" r="62" /></clipPath>
        <filter id="wm_g"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <ellipse cx="120" cy="304" rx="76" ry="6" fill="#000" opacity=".2" />
      <rect x="10" y="38" width="220" height="240" rx="18" fill="url(#wm_b)" stroke="#2a4568" strokeWidth="1.5" />
      <rect x="10" y="248" width="220" height="30" fill="#0f1e2e" />
      <rect x="10" y="262" width="220" height="16" rx="16" fill="#0f1e2e" />
      <rect x="10" y="12" width="220" height="30" rx="12" fill="#0c1825" stroke="#1e3355" strokeWidth=".8" />
      <circle fill="#38bdf8" cy="27" r="4" opacity=".9">
        <animate attributeName="cx" values="38;54;70;86;102;38" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values=".9;.4;.9" dur=".5s" repeatCount="indefinite" />
      </circle>
      <rect x="128" y="16" width="52" height="20" rx="5" fill="#040e1a" stroke="#1e3355" strokeWidth=".7" />
      <text x="154" y="29" textAnchor="middle" fill="#38bdf8" fontSize="8" fontFamily="monospace" opacity=".9">
        {["--", "30°C", "40°C", "60°C", "40°C", "✓"][Math.min(phase + 1, 5)]}
      </text>
      <circle cx="119" cy="155" r="72" fill="#0b1a2a" stroke="#1a3050" strokeWidth="1.2" />
      <circle cx="119" cy="155" r="62" fill="#071e30" />
      <g clipPath="url(#wm_c)">
        <rect x="57" y="110" width="124" height="110" fill="#0ea5e9" opacity=".38" />
        <path fill="#0ea5e9" opacity=".35">
          <animate attributeName="d"
            values="M57,113 Q88,105 119,113 Q150,121 181,113 L181,220 L57,220 Z;M57,108 Q88,118 119,108 Q150,98 181,108 L181,220 L57,220 Z;M57,113 Q88,105 119,113 Q150,121 181,113 L181,220 L57,220 Z"
            dur="1.2s" repeatCount="indefinite" />
        </path>
        {[{ cx: 85, cy: 110, r: 5 }, { cx: 100, cy: 106, r: 4 }, { cx: 119, cy: 108, r: 6.5 },
          { cx: 138, cy: 107, r: 5 }, { cx: 153, cy: 109, r: 4.5 }].map((b, i) => (
          <circle key={i} cx={b.cx} cy={b.cy} r={b.r} fill="white" opacity=".5">
            <animate attributeName="cy" values={`${b.cy};${b.cy - 3};${b.cy}`} dur={`${1.6 + i * 0.18}s`} begin={`${i * 0.15}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {[{ cx: 88 }, { cx: 119 }, { cx: 150 }].map((b, i) => (
          <circle key={i} cx={b.cx} r={3 - i * 0.3} fill="#7dd3fc" opacity=".6">
            <animate attributeName="cy" values="215;100;215" dur={`${1.6 + i * 0.3}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values=".6;0;.6" dur={`${1.6 + i * 0.3}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <g style={{ transformOrigin: "119px 155px", animation: "tl_spin 0.45s linear infinite" }}>
          {holes.map((deg, i) => {
            const rad = (deg * Math.PI) / 180;
            return <circle key={i} cx={119 + 42 * Math.sin(rad)} cy={155 - 42 * Math.cos(rad)} r="6" fill="#071320" stroke="#1e3558" strokeWidth=".8" opacity=".9" />;
          })}
        </g>
        <g transform="translate(119,155)" style={{ transformOrigin: "0 0", animation: "tl_spinr 0.45s linear infinite" }}>
          <circle r="18" fill="#050e1a" stroke="#38bdf8" strokeWidth="1.5" filter="url(#wm_g)" opacity=".95" />
          <text textAnchor="middle" dy=".35em" fill="#38bdf8" fontSize="8" fontWeight="800" fontFamily="'Manrope',sans-serif" letterSpacing="1.5">
            {done ? "✓" : "VFL"}
          </text>
        </g>
      </g>
      <circle cx="119" cy="155" r="62" fill="none" stroke="#1e3558" strokeWidth="2.5" />
    </svg>
  );
};

// ─── Reusable form atoms ───────────────────────────────────────────────────────
const inputSt = (foc) => ({
  width: "100%", padding: "9px 11px", boxSizing: "border-box",
  border: `1.5px solid ${foc ? "#0ea5e9" : "hsl(var(--border))"}`,
  borderRadius: 10, background: "hsl(var(--background))", color: "hsl(var(--foreground))",
  fontSize: 12, fontWeight: 500, fontFamily: "inherit", outline: "none",
  boxShadow: foc ? "0 0 0 2px rgba(14,165,233,.12)" : "none", transition: "all .15s",
});
const FInput    = (p) => { const [f, setF] = useState(false); return <input {...p} style={{ ...inputSt(f), ...(p.style || {}) }} onFocus={() => setF(true)} onBlur={() => setF(false)} />; };
const FTextarea = ({ rows = 3, ...p }) => { const [f, setF] = useState(false); return <textarea rows={rows} {...p} style={{ ...inputSt(f), resize: "vertical", minHeight: 60 }} onFocus={() => setF(true)} onBlur={() => setF(false)} />; };
const FLabel    = ({ children }) => <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".13em", color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>{children}</div>;
const FF        = ({ label, children }) => <div><FLabel>{label}</FLabel>{children}</div>;

const ChipSet = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
    {options.map((o) => (
      <button key={o.val} type="button" onClick={() => onChange(o.val)}
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 13px", borderRadius: 16,
          border: `1.5px solid ${value === o.val ? "#0ea5e9" : "hsl(var(--border))"}`,
          background: value === o.val ? "rgba(14,165,233,.1)" : "hsl(var(--secondary))",
          color: value === o.val ? "#0ea5e9" : "hsl(var(--muted-foreground))",
          fontSize: 12, fontWeight: value === o.val ? 700 : 400, cursor: "pointer", transition: "all .15s",
          transform: value === o.val ? "scale(1.03)" : "scale(1)", fontFamily: "inherit" }}>
        <span style={{ fontSize: 14 }}>{o.icon}</span>{o.label}
      </button>
    ))}
  </div>
);

const OptionCards = ({ options, value, onChange }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
    {options.map((o) => {
      const active     = value === o.val;
      const accent     = o.accentColor || "#0ea5e9";
      const accentBg   = o.accentBg    || "rgba(14,165,233,.08)";
      const accentGlow = o.accentGlow  || "rgba(14,165,233,.15)";
      return (
        <button key={o.val} type="button" onClick={() => onChange(o.val)}
          style={{ padding: "14px 8px", borderRadius: 12, textAlign: "center",
            border: `1.5px solid ${active ? accent : "hsl(var(--border))"}`,
            background: active ? accentBg : "hsl(var(--secondary))",
            cursor: "pointer", transition: "all .18s",
            transform: active ? "scale(1.03)" : "scale(1)", fontFamily: "inherit",
            boxShadow: active ? `0 0 0 3px ${accentGlow}` : "none" }}>
          <div style={{ fontSize: 24, marginBottom: 5, lineHeight: 1 }}>{o.icon}</div>
          {o.badge && (
            <div style={{ display: "inline-block", fontSize: 8, fontWeight: 800, textTransform: "uppercase",
              letterSpacing: ".1em", padding: "2px 7px", borderRadius: 8, marginBottom: 5,
              background: o.badgeBg || "rgba(14,165,233,.12)", color: o.badgeColor || accent,
              border: `1px solid ${o.badgeBorder || "rgba(14,165,233,.3)"}` }}>{o.badge}</div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.3,
            color: active ? accent : "hsl(var(--foreground))", marginBottom: 3 }}>{o.title}</div>
          <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", lineHeight: 1.4 }}>{o.desc}</div>
        </button>
      );
    })}
  </div>
);

// ─── Pricing Tables ────────────────────────────────────────────────────────────
const PRICING = {
  pickup_delivery: {
    standard: { member: 2.50, regular: 2.75, time: "36h" },
    premium:  { member: 2.75, regular: 3.00, time: "24h" },
    express:  { member: 3.00, regular: 3.25, time: "same_day" },
  },
  wash_fold: {
    standard: { price: 2.25, time: "36h" },
    premium:  { price: 2.50, time: "24h" },
    express:  { price: 2.75, time: "same_day" },
  },
};

const getMembershipDiscount = (membershipPlan) => {
  if (!membershipPlan) return null;
  const plan = membershipPlan.toLowerCase();
  if (plan.includes("elite"))                             return { label: "Elite Member", discount: "member", badge: "👑 Elite" };
  if (plan.includes("family"))                            return { label: "Family Plus",  discount: "member", badge: "⭐ Family" };
  if (plan.includes("popular") || plan.includes("most")) return { label: "Member",       discount: "member", badge: "✨ Member" };
  return { label: "Member", discount: "member", badge: "⭐ Member" };
};

// ─── PlanSelector ACTUALIZADO con surcharge para miembros ─────────────────────
const PlanSelector = ({ value, onChange, serviceType, membershipPlan }) => {
  const { t } = useLocale();
  const isPD      = serviceType === "pickup_delivery" || serviceType === "airbnb_host" || serviceType === "commercial";
  const pricing   = isPD ? PRICING.pickup_delivery : PRICING.wash_fold;
  const memberInfo = getMembershipDiscount(membershipPlan);
  const isMember  = !!memberInfo;

  const plans = [
    { val: "standard", icon: "🕒", label: t("Standard", "Estándar") },
    { val: "premium",  icon: "⭐", label: "Premium" },
    { val: "express",  icon: "⚡", label: "Express" },
  ];
  const getTimeLabel = (time) => {
    if (time === "same_day") return t("Same Day", "Mismo día");
    return time;
  };

  return (
    <div>
      {isMember && (
        <div style={{ marginBottom: 8, padding: "6px 12px", borderRadius: 8,
          background: "linear-gradient(135deg,rgba(14,165,233,.08),rgba(56,189,248,.04))",
          border: "1px solid rgba(14,165,233,.25)", display: "flex", alignItems: "center", gap: 7, animation: "tl_panel .2s ease both" }}>
          <span style={{ fontSize: 14 }}>{memberInfo.badge}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#0ea5e9" }}>
            {t("Member pricing applied", "Precio de miembro aplicado")} — {memberInfo.label}
          </span>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
        {plans.map((p) => {
          const tier   = pricing[p.val];
          const active = value === p.val;
          return (
            <button key={p.val} type="button" onClick={() => onChange(p.val)}
              data-testid={`plan-${p.val}`}
              style={{ flex: 1, padding: "12px 8px", borderRadius: 12, textAlign: "center",
                border: `2px solid ${active ? "#0ea5e9" : "hsl(var(--border))"}`,
                background: active ? "rgba(14,165,233,.08)" : "hsl(var(--secondary))",
                cursor: "pointer", transition: "all .15s",
                transform: active ? "scale(1.02)" : "scale(1)",
                fontFamily: "inherit", position: "relative" }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{p.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: active ? "#0ea5e9" : "hsl(var(--foreground))", letterSpacing: "0.02em" }}>{p.label}</div>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 2, fontWeight: 600 }}>{getTimeLabel(tier.time)}</div>
              {isPD ? (
                <div style={{ marginTop: 6 }}>
                  {isMember ? (
                    <div>
                      {/* Precio efectivo para miembro con allowance */}
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#059669" }}>
                        ${tier.member.toFixed(2)}/lb
                        <span style={{ fontSize: 9, color: "#0ea5e9", fontWeight: 700, marginLeft: 3 }}>
                          MEMBER
                        </span>
                      </div>
                      {/* Mostrar surcharge si aplica */}
                      {p.val === "premium" && (
                        <div style={{ fontSize: 9, color: "#f59e0b", fontWeight: 600, marginTop: 2 }}>
                          +$0.25/lb en allowance
                        </div>
                      )}
                      {p.val === "express" && (
                        <div style={{ fontSize: 9, color: "#f97316", fontWeight: 600, marginTop: 2 }}>
                          +$0.75/lb en allowance
                        </div>
                      )}
                      {p.val === "standard" && (
                        <div style={{ fontSize: 9, color: "#059669", fontWeight: 600, marginTop: 2 }}>
                          ✓ incluido en membresía
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#059669" }}>
                        ${tier.member.toFixed(2)}/lb
                        <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500, marginLeft: 3 }}>{t("Member", "Miembro")}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>
                        ${tier.regular.toFixed(2)}/lb
                        <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500, marginLeft: 3 }}>{t("Regular", "Regular")}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "#059669" }}>
                  ${tier.price.toFixed(2)}/lb
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const RecurrenceSelector = ({ value, onChange, endDate, onEndDateChange, selectedDays, onDaysChange, locale, t }) => {
  const isRecurring      = value && value !== "once";
  const showDaysSelector = value === "twice_week";

  return (
    <div style={{ animation: "tl_panel .25s ease both" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {RECURRENCE_OPTIONS.map((o) => {
          const active = value === o.val;
          return (
            <button key={o.val} type="button" onClick={() => onChange(o.val)}
              style={{ flex: "1 1 calc(50% - 8px)", minWidth: 120, padding: "10px 8px", borderRadius: 10,
                textAlign: "center", border: `1.5px solid ${active ? "#0ea5e9" : "hsl(var(--border))"}`,
                background: active ? "rgba(14,165,233,.08)" : "hsl(var(--secondary))",
                cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
                transform: active ? "scale(1.02)" : "scale(1)" }}>
              <span style={{ fontSize: 18, display: "block", marginBottom: 3 }}>{o.icon}</span>
              <span style={{ fontSize: 11, fontWeight: active ? 700 : 500,
                color: active ? "#0ea5e9" : "hsl(var(--foreground))" }}>
                {locale === "es" ? o.es : o.en}
              </span>
            </button>
          );
        })}
      </div>
      {showDaysSelector && (
        <WeekdaySelector selectedDays={selectedDays} onChange={onDaysChange} locale={locale} t={t} />
      )}
      {isRecurring && (
        <div style={{ marginTop: 12, animation: "tl_panel .2s ease both" }}>
          <div style={{ padding: "10px 14px", borderRadius: 9,
            background: "linear-gradient(135deg,rgba(14,165,233,.06),rgba(56,189,248,.03))",
            border: "1px solid rgba(14,165,233,.2)", marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "#0ea5e9", fontWeight: 700, marginBottom: 2 }}>
              🔄 {t("Recurring schedule activated", "Programación recurrente activada")}
            </div>
            <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))" }}>
              {t("Pickups will be auto-scheduled based on your first pickup date.",
                 "Los pickups se programarán automáticamente desde tu primera fecha.")}
            </div>
          </div>
          <FF label={t("Recurring until (optional)", "Recurrente hasta (opcional)")}>
            <FInput type="date" value={endDate || ""}
              onChange={(e) => onEndDateChange(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              style={{ cursor: "pointer" }} />
          </FF>
        </div>
      )}
    </div>
  );
};

// ─── Service Info Panels ───────────────────────────────────────────────────────
const PickupDeliveryInfoPanel = ({ t }) => (
  <div style={{ padding: "13px 15px", borderRadius: 11, marginTop: 8,
    background: "linear-gradient(135deg,rgba(14,165,233,.06),rgba(56,189,248,.04))",
    border: "1px solid rgba(14,165,233,.25)", animation: "tl_panel .25s ease both" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
      <span style={{ fontSize: 17 }}>🚚</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#0ea5e9" }}>{t("Pickup & Delivery Service", "Servicio de Recogida y Entrega")}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 12px" }}>
      {[
        { icon: "⚡", en: "Same-day available",      es: "Disponible el mismo día" },
        { icon: "🌿", en: "Eco-friendly detergents",  es: "Detergentes ecológicos" },
        { icon: "🩸", en: "Stain treatment",          es: "Tratamiento de manchas" },
        { icon: "📦", en: "Folding & packaging",      es: "Doblado y empaquetado" },
        { icon: "✅", en: "Satisfaction guaranteed",  es: "Garantía de satisfacción" },
      ].map((f) => (
        <div key={f.icon} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "hsl(var(--foreground))" }}>
          <span style={{ fontSize: 13 }}>{f.icon}</span><span>{t(f.en, f.es)}</span>
        </div>
      ))}
    </div>
  </div>
);

const AirbnbInfoPanel = ({ t }) => (
  <div style={{ padding: "13px 15px", borderRadius: 11, marginTop: 8,
    background: "linear-gradient(135deg,rgba(255,92,37,.06),rgba(255,56,92,.04))",
    border: "1px solid rgba(255,92,37,.25)", animation: "tl_panel .25s ease both" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
      <span style={{ fontSize: 17 }}>🏠</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#ff5c25" }}>{t("Airbnb Host Service", "Servicio para Anfitriones Airbnb")}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 12px" }}>
      {[
        { icon: "🛏️", en: "Bed linens & towels",  es: "Sábanas y toallas" },
        { icon: "⚡",  en: "Priority same-day",     es: "Prioridad mismo día" },
        { icon: "📦",  en: "Folded & bag-ready",    es: "Doblado listo en bolsa" },
        { icon: "🔄",  en: "Recurring weekly plan", es: "Plan semanal recurrente" },
      ].map((f) => (
        <div key={f.icon} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "hsl(var(--foreground))" }}>
          <span style={{ fontSize: 13 }}>{f.icon}</span><span>{t(f.en, f.es)}</span>
        </div>
      ))}
    </div>
  </div>
);

const CommercialInfoPanel = ({ t }) => (
  <div style={{ padding: "13px 15px", borderRadius: 11, marginTop: 8,
    background: "linear-gradient(135deg,rgba(99,102,241,.06),rgba(79,70,229,.04))",
    border: "1px solid rgba(99,102,241,.25)", animation: "tl_panel .25s ease both" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
      <span style={{ fontSize: 17 }}>🏢</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5" }}>{t("Commercial / B2B Service", "Servicio Comercial / B2B")}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 12px" }}>
      {[
        { icon: "💰", en: "Bulk pricing",     es: "Precios por volumen" },
        { icon: "📅", en: "Weekly contracts", es: "Contratos semanales" },
        { icon: "⚡", en: "Fast turnaround",  es: "Plazo rápido" },
        { icon: "⚙️", en: "Customizable",     es: "Personalizable" },
      ].map((f) => (
        <div key={f.icon} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "hsl(var(--foreground))" }}>
          <span style={{ fontSize: 13 }}>{f.icon}</span><span>{t(f.en, f.es)}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── ADD-ON SERVICES ──────────────────────────────────────────────────────────
const PRICE_UNIT_LABELS = {
  per_lb:    { en: "/ lb",    es: "/ lb"    },
  per_order: { en: "/ order", es: "/ orden" },
  per_month: { en: "/ month", es: "/ mes"   },
  per_item:  { en: "/ item",  es: "/ pieza" },
};

const SERVICE_CATEGORY_ICONS = {
  "wash & fold": "🧺", "pickup": "🚚", "delivery": "🚚",
  "dry cleaning": "👔", "shoe": "👟", "ironing": "🔄",
  "bedding": "🛏️", "membership": "⭐", "express": "⚡",
  "commercial": "🏢", "airbnb": "🏠",
};

function getServiceIcon(name = "", category = "") {
  const search = `${name} ${category}`.toLowerCase();
  for (const [key, icon] of Object.entries(SERVICE_CATEGORY_ICONS)) {
    if (search.includes(key)) return icon;
  }
  return "✨";
}

const AddonServicesSection = ({ services, selectedIds, onToggle, t, locale }) => {
  if (!services || services.length === 0) return null;
  return (
    <div style={{ animation: "tl_panel .3s ease both" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
        paddingBottom: 8, borderBottom: "1px solid hsl(var(--border))" }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(14,165,233,.1)", display: "flex",
          alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>✨</div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "hsl(var(--foreground))" }}>
            {t("Add-on Services", "Servicios Adicionales")}
          </div>
          <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", marginTop: 1 }}>
            {t("Optional — select any extras for your order", "Opcional — selecciona extras para tu pedido")}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {services.map((svc) => {
          const active    = selectedIds.has(svc.id);
          const icon      = getServiceIcon(svc.name, svc.category || "");
          const unitLabel = PRICE_UNIT_LABELS[svc.price_unit] || { en: "", es: "" };
          const unit      = locale === "es" ? unitLabel.es : unitLabel.en;
          return (
            <button key={svc.id} type="button" onClick={() => onToggle(svc)}
              style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 12px",
                borderRadius: 10, textAlign: "left", width: "100%",
                border: `1.5px solid ${active ? "#0ea5e9" : "hsl(var(--border))"}`,
                background: active ? "rgba(14,165,233,.06)" : "hsl(var(--secondary))",
                cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
                transform: active ? "scale(1.005)" : "scale(1)",
                boxShadow: active ? "0 0 0 3px rgba(14,165,233,.1)" : "none" }}>
              <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                border: `2px solid ${active ? "#0ea5e9" : "hsl(var(--border))"}`,
                background: active ? "#0ea5e9" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }}>
                {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              </div>
              <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: active ? "rgba(14,165,233,.12)" : "hsl(var(--background))",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, transition: "all .15s" }}>{icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: active ? "#0ea5e9" : "hsl(var(--foreground))", transition: "color .15s" }}>
                  {svc.name}
                </div>
                {svc.description && (
                  <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", lineHeight: 1.4, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{svc.description}</div>
                )}
              </div>
              {svc.price != null && (
                <div style={{ flexShrink: 0, textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: active ? "#0ea5e9" : "#059669" }}>${svc.price}</div>
                  {unit && <div style={{ fontSize: 9, color: "hsl(var(--muted-foreground))", fontWeight: 500 }}>{unit}</div>}
                </div>
              )}
            </button>
          );
        })}
      </div>
      {selectedIds.size > 0 && (
        <div style={{ marginTop: 9, padding: "7px 11px", borderRadius: 8,
          background: "rgba(14,165,233,.08)", border: "1px solid rgba(14,165,233,.2)",
          display: "flex", alignItems: "center", gap: 7, animation: "tl_panel .2s ease both" }}>
          <span style={{ fontSize: 13 }}>✅</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#0ea5e9" }}>
            {selectedIds.size} {t(selectedIds.size === 1 ? "add-on selected" : "add-ons selected",
                                   selectedIds.size === 1 ? "servicio adicional seleccionado" : "servicios adicionales seleccionados")}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Temp selectors ────────────────────────────────────────────────────────────
const TempRow = ({ value, onChange, options }) => (
  <div style={{ display: "flex", gap: 6 }}>
    {options.map((o) => (
      <button key={o.val} type="button" onClick={() => onChange(o.val)}
        style={{ flex: 1, padding: "9px 4px", borderRadius: 9, textAlign: "center",
          border: `1.5px solid ${value === o.val ? "#0ea5e9" : "hsl(var(--border))"}`,
          background: value === o.val ? "rgba(14,165,233,.1)" : "hsl(var(--secondary))",
          cursor: "pointer", transition: "all .15s", minWidth: 0,
          transform: value === o.val ? "scale(1.04)" : "scale(1)", fontFamily: "inherit" }}>
        <div style={{ fontSize: 18, marginBottom: 2 }}>{o.icon}</div>
        <div style={{ fontSize: 10, fontWeight: 600, color: value === o.val ? "#0ea5e9" : "hsl(var(--foreground))" }}>{o.label}</div>
        <div style={{ fontSize: 9, color: "hsl(var(--muted-foreground))", marginTop: 1 }}>{o.sub}</div>
      </button>
    ))}
  </div>
);

const WASH_TEMP_OPTIONS = [
  { val: "cold", icon: "❄️",  label: "Cold",  sub: "≤30°C"    },
  { val: "warm", icon: "🌡️", label: "Warm",  sub: "40°C"     },
  { val: "hot",  icon: "🔥",  label: "Hot",   sub: "60°C+"    },
  { val: "any",  icon: "✨",  label: "Any",   sub: "Trust us" },
];

const DRY_TEMP_OPTIONS = [
  { val: "low",    icon: "🌬️", label: "Low",     sub: "Delicates"  },
  { val: "medium", icon: "☀️", label: "Medium",  sub: "Normal"     },
  { val: "high",   icon: "🔆", label: "High",    sub: "Heavy duty" },
  { val: "air",    icon: "🌿", label: "Air dry", sub: "No heat"    },
];

const getTimeSlotAvailability = (pickupDate, servicePlan, todayStr) => {
  const now              = new Date();
  const nowMinutes       = now.getHours() * 60 + now.getMinutes();
  const CUTOFF_MORNING   = 10 * 60;
  const CUTOFF_AFTERNOON = 16 * 60;
  const isToday          = pickupDate === todayStr;
  const isExpress        = servicePlan === "express";

  if (!isToday) return { morning: true, afternoon: true, noSlots: false };
  if (!isExpress) return { morning: true, afternoon: true, noSlots: false };

  const morningAvailable   = nowMinutes < CUTOFF_MORNING;
  const afternoonAvailable = nowMinutes < CUTOFF_AFTERNOON;
  return { morning: morningAvailable, afternoon: afternoonAvailable, noSlots: !morningAvailable && !afternoonAvailable };
};

const TimeSlots = ({ value, onChange, locale, pickupDate, servicePlan, todayStr }) => {
  const availability = getTimeSlotAvailability(pickupDate, servicePlan, todayStr);
  const slots = [
    { val: "8am-12pm",  icon: "🌅", time: "8:00 AM – 12:00 PM", note: locale === "es" ? "Mañana"   : "Morning",   cutoff: locale === "es" ? "Disponible antes de las 10:00 AM" : "Available before 10:00 AM", available: availability.morning   },
    { val: "2pm-6pm",   icon: "🌆", time: "2:00 PM – 6:00 PM",  note: locale === "es" ? "Tarde"    : "Afternoon", cutoff: locale === "es" ? "Disponible antes de las 4:00 PM"  : "Available before 4:00 PM",  available: availability.afternoon },
  ];

  return (
    <div>
      {availability.noSlots && pickupDate === todayStr && servicePlan === "express" && (
        <div style={{ padding: "10px 13px", borderRadius: 9, marginBottom: 10,
          background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.35)",
          display: "flex", alignItems: "flex-start", gap: 8, animation: "tl_panel .2s ease both" }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⏰</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", marginBottom: 2 }}>
              {locale === "es" ? "Ya no hay ventanas disponibles para hoy" : "No more time windows available for today"}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {slots.map((o) => {
          const isSelected = value === o.val;
          const isDisabled = !o.available;
          return (
            <button key={o.val} type="button"
              onClick={() => !isDisabled && onChange(o.val)}
              disabled={isDisabled}
              style={{
                padding: "12px 8px", borderRadius: 10, textAlign: "center",
                border: `1.5px solid ${isSelected ? "#0ea5e9" : "hsl(var(--border))"}`,
                background: isSelected ? "rgba(14,165,233,.08)" : "hsl(var(--secondary))",
                cursor: isDisabled ? "not-allowed" : "pointer",
                transition: "all .15s",
                transform: isSelected ? "scale(1.02)" : "scale(1)",
                fontFamily: "inherit", opacity: isDisabled ? 0.45 : 1,
                position: "relative", overflow: "hidden",
              }}>
              {isDisabled && (
                <div style={{ position: "absolute", inset: 0,
                  backgroundImage: "repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(0,0,0,.04) 5px, rgba(0,0,0,.04) 10px)",
                  borderRadius: 10, pointerEvents: "none" }} />
              )}
              <div style={{ fontSize: 18, marginBottom: 4, filter: isDisabled ? "grayscale(1)" : "none" }}>{o.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: isSelected ? "#0ea5e9" : isDisabled ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))" }}>
                {isDisabled ? <s>{o.time}</s> : o.time}
              </div>
              <div style={{ fontSize: 9, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                {isDisabled ? o.cutoff : o.note}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SumBlock = ({ title, rows }) => (
  <div style={{ padding: "10px 12px", borderRadius: 9, background: "hsl(var(--secondary))", border: "0.5px solid hsl(var(--border))" }}>
    <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".12em", color: "hsl(var(--muted-foreground))", marginBottom: 6 }}>{title}</div>
    {rows.filter(([, v]) => v).map(([k, v]) => (
      <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0", fontSize: 11 }}>
        <span style={{ color: "hsl(var(--muted-foreground))" }}>{k}</span>
        <span style={{ fontWeight: 600, color: "hsl(var(--foreground))", textAlign: "right" }}>{v}</span>
      </div>
    ))}
  </div>
);

// ─── Conveyor track ────────────────────────────────────────────────────────────
const ConveyorTrack = ({ cur, locale, onStageClick }) => {
  const stageRefs           = useRef([]);
  const trackRef            = useRef(null);
  const [riderLeft, setRiderLeft] = useState(0);

  useEffect(() => {
    if (stageRefs.current[cur] && trackRef.current) {
      const tRect = trackRef.current.getBoundingClientRect();
      const sRect = stageRefs.current[cur].getBoundingClientRect();
      setRiderLeft(sRect.left - tRect.left + sRect.width / 2 - 26);
    }
  }, [cur]);

  return (
    <div style={{ background: "linear-gradient(150deg,#0b1929 0%,#081320 60%,#040c16 100%)", borderRadius: 16, padding: "20px 0 14px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.022) 1px,transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />
      <div style={{ overflowX: "auto", overflowY: "visible", padding: "0 16px", WebkitOverflowScrolling: "touch" }} className="scrollbar-hide">
        <div ref={trackRef} style={{ display: "flex", alignItems: "flex-start", minWidth: "max-content", padding: "10px 8px 4px", position: "relative" }}>
          <div style={{ position: "absolute", top: 38, left: 0, right: 0, height: 4,
            background: "repeating-linear-gradient(90deg,#1e3558 0,#1e3558 18px,#0b1929 18px,#0b1929 24px)",
            borderRadius: 2, zIndex: 0 }} />
          <div style={{ position: "absolute", top: 10, left: riderLeft,
            transition: "left .6s cubic-bezier(.34,1.56,.64,1)", zIndex: 3, pointerEvents: "none",
            animation: "tl_float 4s ease-in-out infinite" }}>
            <RiderMachine step={cur} />
          </div>
          {STAGES.map((s, i) => (
            <React.Fragment key={`stage-group-${i}`}>
              {i > 0 && (
                <div key={`conn-${i}`} style={{ width: 28, height: 4, marginTop: 26, flexShrink: 0, zIndex: 0, position: "relative",
                  background: i <= cur ? "#0ea5e9" : "#1e3558", transition: "background .3s" }}>
                  <div style={{ position: "absolute", top: -3, right: -5, width: 0, height: 0,
                    borderTop: "5px solid transparent", borderBottom: "5px solid transparent",
                    borderLeft: `5px solid ${i <= cur ? "#0ea5e9" : "#1e3558"}`, transition: "border-left-color .3s" }} />
                </div>
              )}
              <div key={`stage-${i}`}
                ref={(el) => (stageRefs.current[i] = el)}
                onClick={() => i < cur && onStageClick(i)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0,
                  cursor: i < cur ? "pointer" : "default", position: "relative", zIndex: 1, width: 112, flexShrink: 0 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 20, flexShrink: 0, position: "relative",
                  background: i < cur ? "rgba(14,165,233,.15)" : i === cur ? "#0ea5e9" : "#0b1929",
                  border: `2.5px solid ${i < cur ? "#0ea5e9" : i === cur ? "#38bdf8" : "#1e3558"}`,
                  boxShadow: i === cur ? "0 0 0 6px rgba(14,165,233,.2)" : "none",
                  transform: i === cur ? "scale(1.18)" : "scale(1)",
                  transition: "all .25s cubic-bezier(.34,1.56,.64,1)" }}>
                  {s.icon}
                  {i === cur && <div style={{ position: "absolute", inset: -8, borderRadius: "50%", border: "2px solid rgba(14,165,233,.35)", animation: "tl_pulse 1.6s ease-out infinite" }} />}
                  {i < cur && <div style={{ position: "absolute", bottom: -2, right: -2, width: 16, height: 16,
                    borderRadius: "50%", background: "#0ea5e9", color: "white", fontSize: 9,
                    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>✓</div>}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em",
                  color: i === cur ? "#38bdf8" : i < cur ? "rgba(255,255,255,.6)" : "rgba(255,255,255,.25)",
                  marginTop: 8, whiteSpace: "nowrap", transition: "color .2s" }}>
                  {locale === "es" ? s.es : s.en}
                </div>
                <div style={{ fontSize: 9, color: i === cur ? "rgba(14,165,233,.7)" : "rgba(255,255,255,.2)",
                  textAlign: "center", maxWidth: 96, lineHeight: 1.4, transition: "color .2s" }}>
                  {locale === "es" ? s.subES : s.subEN}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── localStorage keys ─────────────────────────────────────────────────────────
const LS_FORM_KEY = "vfl_pickup_form";
const LS_STEP_KEY = "vfl_pickup_step";
const LS_ADDR_KEY = "vfl_pickup_addr_validation";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", dialCode: "+1", dialIso: "US",
  contact_method: "", sms_consent: false,
  address_line1: "", address_line2: "", city: "", state: "", zip_code: "", addr_notes: "",
  service_type: "", service_plan: "",
  recurrence: "once", recurrence_end_date: "", recurrence_days: [],
  wash_temp: "", dry_temp: "", notes: "",
  pickup_date: "", pickup_time: "", terms: false,
  distance_miles: null,
  delivery_fee: null,
};

const loadSavedForm = () => {
  try {
    const saved = localStorage.getItem(LS_FORM_KEY);
    if (saved) return { ...EMPTY, ...JSON.parse(saved) };
  } catch {}
  return { ...EMPTY };
};

const loadSavedStep = () => {
  try {
    const saved = localStorage.getItem(LS_STEP_KEY);
    if (saved !== null) {
      const n = parseInt(saved, 10);
      if (!isNaN(n) && n >= 0 && n <= 4) return n;
    }
  } catch {}
  return 0;
};

const loadSavedAddrValidation = () => {
  try {
    const saved = localStorage.getItem(LS_ADDR_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function SchedulePickup() {
  const { t, locale } = useLocale();
  useSEO({
    title: t(
      "Schedule a Laundry Pickup | Ventura Fresh Laundry — Ventura, CA & Oxnard",
      "Programa tu Recogida de Lavandería | Ventura Fresh Laundry — Ventura, CA y Oxnard"
    ),
    description: t(
      "Book a free laundry pickup & delivery in Ventura, CA or Oxnard in minutes. Same-day and recurring plans available for wash & fold and dry cleaning.",
      "Programa una recogida y entrega de lavandería gratis en Ventura, CA u Oxnard en minutos. Planes el mismo día y recurrentes para lavado y doblado y tintorería."
    ),
    path: "/schedule-pickup",
  });
  const topRef  = useRef(null);
  const formRef = useRef(null);

  const [cur, setCur]           = useState(() => loadSavedStep());
  const [formKey, setFormKey]   = useState(0);
  const [form, setForm]         = useState(() => loadSavedForm());
  const [submitting, setSubmitting] = useState(false);
  const [washPhase, setWashPhase]   = useState(-1);
  const [washDone, setWashDone]     = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null); // { temp_token, message } | null
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying]   = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const [membershipInfo, setMembershipInfo] = useState(null);
  const [addonServices, setAddonServices]   = useState([]);
  const [selectedAddons, setSelectedAddons] = useState(new Map());
  const [showResumeBanner, setShowResumeBanner] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_FORM_KEY);
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      return !!(parsed.first_name || parsed.email || parsed.address_line1);
    } catch { return false; }
  });

  const [distanceChecking, setDistanceChecking] = useState(false);
  const [distanceValid,    setDistanceValid]    = useState(() => {
    const cached = loadSavedAddrValidation();
    return cached?.valid ?? false;
  });
  const [distanceError,    setDistanceError]    = useState(() => {
    const cached = loadSavedAddrValidation();
    return cached?.error ?? "";
  });
  const [distanceMiles,    setDistanceMiles]    = useState(() => {
    const cached = loadSavedAddrValidation();
    return cached?.distanceMiles ?? null;
  });
  const [deliveryFee,      setDeliveryFee]      = useState(() => {
    const cached = loadSavedAddrValidation();
    return cached?.deliveryFee ?? null;
  });
  const lastValidatedAddrRef = useRef(null);
  const checkDistanceDebounce = useRef(null);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // FIX: helper centralizado para limpiar TODO el estado de validación de
  // dirección de golpe (estado en memoria + lo cacheado en localStorage).
  // Antes, al editar un campo de dirección después de haber sido validada,
  // el "✅ verificado" con millas/tarifa de la dirección ANTERIOR se quedaba
  // visible durante los 700ms del debounce (o incluso más, si el usuario
  // borraba y volvía a escribir), mostrando información obsoleta.
  const clearAddressValidation = useCallback(() => {
    setDistanceValid(false);
    setDistanceError("");
    setDistanceMiles(null);
    setDeliveryFee(null);
    setDistanceChecking(false);
    lastValidatedAddrRef.current = null;
    try { localStorage.removeItem(LS_ADDR_KEY); } catch {}
  }, []);

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const todayStr = formatLocalDate(new Date());

  const getMinPickupDate = (plan) => {
    const today    = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (plan === "express") return formatLocalDate(today);
    return formatLocalDate(tomorrow);
  };

  const getMaxPickupDate = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 2);
    return formatLocalDate(d);
  };

  const isValidDateFormat = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");

  const validatePickupDateValue = (value) => {
    if (!value) return { ok: true, reason: null };
    if (!isValidDateFormat(value)) return { ok: false, reason: "format" };
    const minDate = getMinPickupDate(form.service_plan || "standard");
    const maxDate = getMaxPickupDate();
    const year = parseInt(value.slice(0, 4), 10);
    const currentYear = new Date().getFullYear();
    if (year < currentYear - 1 || year > currentYear + 5) return { ok: false, reason: "year" };
    if (value < minDate) return { ok: false, reason: "past", minDate };
    if (value > maxDate) return { ok: false, reason: "future", maxDate };
    try {
      const parts = value.split("-").map(Number);
      const dt = new Date(parts[0], parts[1] - 1, parts[2]);
      if (dt.getFullYear() !== parts[0] || dt.getMonth() + 1 !== parts[1] || dt.getDate() !== parts[2]) {
        return { ok: false, reason: "invalid" };
      }
    } catch {
      return { ok: false, reason: "invalid" };
    }
    return { ok: true, reason: null };
  };

  const handlePickupDateChange = (rawValue) => {
    if (!rawValue) { setF("pickup_date", ""); return; }
    const check = validatePickupDateValue(rawValue);
    if (!check.ok) {
      if (check.reason === "past") {
        toast.error(t("Pickup date cannot be in the past. Select a date from today onwards.",
                       "La fecha de recogida no puede ser pasada. Selecciona una fecha desde hoy en adelante."));
      } else if (check.reason === "year") {
        toast.error(t("Enter a valid year for the pickup date.",
                       "Ingresa un año válido para la fecha de recogida."));
      } else if (check.reason === "format" || check.reason === "invalid") {
        toast.error(t("Enter a valid date (YYYY-MM-DD).",
                       "Ingresa una fecha válida (AAAA-MM-DD)."));
      } else if (check.reason === "future") {
        toast.error(t("Pickup date is too far in the future.",
                       "La fecha de recogida está muy adelantada en el futuro."));
      }
      setF("pickup_date", "");
      return;
    }
    setF("pickup_date", rawValue);
  };

  useEffect(() => {
    if (!form.pickup_date || !form.pickup_time) return;
    const availability = getTimeSlotAvailability(form.pickup_date, form.service_plan, todayStr);
    if (form.pickup_time === "8am-12pm" && !availability.morning) {
      setF("pickup_time", "");
      toast.info(t("Morning window no longer available.", "La ventana de mañana ya no está disponible."));
    }
    if (form.pickup_time === "2pm-6pm" && !availability.afternoon) {
      setF("pickup_time", "");
      toast.info(t("Afternoon window no longer available today.", "La ventana de tarde ya no está disponible hoy."));
    }
  }, [form.pickup_date, form.service_plan]);

  useEffect(() => {
    if (form.pickup_date && form.service_plan) {
      const minDate = getMinPickupDate(form.service_plan);
      if (form.pickup_date < minDate) {
        setF("pickup_date", "");
        toast.info(t("Pickup date reset — plan does not allow same-day.", "Se reinició la fecha — el plan no permite mismo día."));
      }
    }
  }, [form.service_plan]);

  useEffect(() => {
    try {
      const cd = localStorage.getItem("customer_data");
      if (cd) {
        const c = JSON.parse(cd);
        if (c.membership_plan || c.membership_status === "active") {
          setMembershipInfo({ plan: c.membership_plan, status: c.membership_status });
        }
      }
    } catch {}
  }, []);

  const checkDistance = useCallback(async (address) => {
    if (!address || address.trim().length < 10) {
      setDistanceError("");
      setDistanceValid(false);
      setDistanceMiles(null);
      setDeliveryFee(null);
      return false;
    }

    if (lastValidatedAddrRef.current === address && distanceValid) {
      return true;
    }

    setDistanceChecking(true);
    setDistanceError("");

    try {
      const res = await axios.post(`${API}/store/check-address`, { address });

      if (res.data?.valid === false) {
        const errMsg = res.data.error || t("Address outside delivery area", "Dirección fuera del área de entrega");
        setDistanceError(errMsg);
        setDistanceValid(false);
        setDistanceMiles(null);
        setDeliveryFee(null);
        lastValidatedAddrRef.current = address;
        try { localStorage.setItem(LS_ADDR_KEY, JSON.stringify({ valid: false, error: errMsg, distanceMiles: null, deliveryFee: null, address })); } catch {}
        return false;
      } else if (res.data?.valid === true) {
        const miles = res.data.distance_miles || null;
        const fee   = res.data.delivery_fee !== undefined ? parseFloat(res.data.delivery_fee) : null;
        setDistanceError("");
        setDistanceValid(true);
        setDistanceMiles(miles);
        setDeliveryFee(fee);
        if (miles) setF("distance_miles", miles);
        if (fee !== null) setF("delivery_fee", fee);
        lastValidatedAddrRef.current = address;
        try { localStorage.setItem(LS_ADDR_KEY, JSON.stringify({ valid: true, error: "", distanceMiles: miles, deliveryFee: fee, address })); } catch {}
        return true;
      } else {
        setDistanceError(t("Could not verify address", "No se pudo verificar la dirección"));
        setDistanceValid(false);
        setDistanceMiles(null);
        setDeliveryFee(null);
        return false;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error
        || t("Could not verify address distance", "No se pudo verificar la distancia");
      setDistanceError(msg);
      setDistanceValid(false);
      setDistanceMiles(null);
      setDeliveryFee(null);
      return false;
    } finally {
      setDistanceChecking(false);
    }
  }, [t, distanceValid]);

  useEffect(() => {
    if (checkDistanceDebounce.current) clearTimeout(checkDistanceDebounce.current);

    const fullAddress = [form.address_line1, form.city, form.state, form.zip_code]
      .filter(Boolean).join(", ");

    if (!fullAddress || fullAddress.length < 10) {
      if (!distanceValid) {
        setDistanceError("");
        setDistanceMiles(null);
        setDeliveryFee(null);
      }
      return;
    }

    const cached = loadSavedAddrValidation();
    if (cached && cached.address === fullAddress) {
      setDistanceValid(cached.valid);
      setDistanceError(cached.error || "");
      setDistanceMiles(cached.distanceMiles);
      setDeliveryFee(cached.deliveryFee);
      if (cached.distanceMiles) setF("distance_miles", cached.distanceMiles);
      if (cached.deliveryFee !== undefined) setF("delivery_fee", cached.deliveryFee);
      lastValidatedAddrRef.current = fullAddress;
      return;
    }

    // FIX: en cuanto la dirección deja de coincidir con la última validada,
    // se limpia el estado de inmediato (en vez de esperar los 700ms del
    // debounce de abajo). Así la UI no muestra un "✅ verificado" obsoleto
    // mientras el usuario sigue escribiendo/editando.
    if (lastValidatedAddrRef.current && lastValidatedAddrRef.current !== fullAddress) {
      setDistanceValid(false);
      setDistanceError("");
      setDistanceMiles(null);
      setDeliveryFee(null);
    }

    checkDistanceDebounce.current = setTimeout(() => {
      checkDistance(fullAddress);
    }, 700);

    return () => clearTimeout(checkDistanceDebounce.current);
  }, [form.address_line1, form.city, form.state, form.zip_code]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/services?active_only=true`);
        if (res.ok) setAddonServices(await res.json());
      } catch {}
    };
    load();
  }, []);

  const toggleAddon = (svc) => {
    setSelectedAddons(prev => {
      const next = new Map(prev);
      if (next.has(svc.id)) next.delete(svc.id);
      else next.set(svc.id, svc);
      return next;
    });
  };

  useEffect(() => {
    try { localStorage.setItem(LS_FORM_KEY, JSON.stringify(form)); } catch {}
  }, [form]);

  useEffect(() => {
    try { localStorage.setItem(LS_STEP_KEY, String(cur)); } catch {}
  }, [cur]);

  useEffect(() => {
    try {
      const cd = localStorage.getItem("customer_data");
      if (!cd) return;
      const c = JSON.parse(cd);

      const nameParts = (c.name || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName  = nameParts.slice(1).join(" ") || "";

      const rawPhone = (c.phone || "").replace(/^\+\d{1,3}\s?/, "").trim();

      let street   = c.address_line1 || "";
      let city     = c.city          || "";
      let state    = c.state         || "";
      let zip      = c.zip_code      || "";

      if (!street && c.address) {
        const parts = c.address.split(",").map(s => s.trim()).filter(Boolean);
        if (parts.length >= 1) street = parts[0];
        if (parts.length === 3) {
          city = parts[1];
          const lastPart = parts[2].split(/\s+/);
          if (lastPart.length >= 2) {
            state = lastPart[0];
            zip   = lastPart.slice(1).join("");
          } else {
            state = lastPart[0];
          }
        } else if (parts.length === 4) {
          city  = parts[1];
          state = parts[2];
          zip   = parts[3];
        } else if (parts.length >= 5) {
          street = parts.slice(0, parts.length - 3).join(", ");
          city   = parts[parts.length - 3];
          state  = parts[parts.length - 2];
          zip    = parts[parts.length - 1];
        }

        if (state && !zip) {
          const stateZip = state.split(/\s+/);
          if (stateZip.length === 2) {
            state = stateZip[0];
            zip   = stateZip[1];
          }
        }
      }

      setForm(p => ({
        ...p,
        first_name:    p.first_name    || firstName,
        last_name:     p.last_name     || lastName,
        email:         p.email         || c.email    || "",
        phone:         p.phone         || rawPhone,
        address_line1: p.address_line1 || street,
        city:          p.city          || city,
        state:         p.state         || state,
        zip_code:      p.zip_code      || zip,
      }));
    } catch {}
  }, []);

  const scrollToForm      = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goTo              = (n) => { setCur(n); setFormKey(k => k + 1); scrollToForm(); };
  const clearSavedSession = () => {
    try {
      localStorage.removeItem(LS_FORM_KEY);
      localStorage.removeItem(LS_STEP_KEY);
      localStorage.removeItem(LS_ADDR_KEY);
    } catch {}
  };

  const showRecurrenceSelector = !!form.service_type;

  const validate = () => {
    const err = (msg) => { toast.error(msg); return false; };

    if (cur === 0) {
      if (!form.first_name.trim())   return err(t("Enter your first name",   "Ingresa tu nombre"));
      if (!form.last_name.trim())    return err(t("Enter your last name",    "Ingresa tu apellido"));
      if (!form.email.includes("@")) return err(t("Enter a valid email",     "Correo inválido"));
      if (!form.phone.trim())        return err(t("Enter your phone",        "Ingresa tu teléfono"));
      if (!form.contact_method)      return err(t("Select a contact method", "Selecciona método de contacto"));
      if (form.contact_method === "text" && !form.sms_consent)
        return err(t("Accept SMS consent", "Acepta el consentimiento SMS"));
    }

    if (cur === 1) {
      if (!form.address_line1.trim()) return err(t("Enter your address", "Ingresa tu dirección"));
      if (!form.city.trim())          return err(t("Enter your city",    "Ingresa tu ciudad"));
      if (!form.state.trim())         return err(t("Enter your state",   "Ingresa tu estado"));
      if (!form.zip_code.trim())      return err(t("Enter your ZIP",     "Ingresa tu código postal"));
      if (distanceError)              { toast.error(distanceError); return false; }
      // FIX: antes esta validación solo se aplicaba si `zip_code.length >= 5`,
      // lo que permitía saltarse por completo la verificación de dirección
      // con un ZIP corto/incompleto. Ahora la dirección SIEMPRE debe estar
      // verificada (distanceValid === true) para avanzar, sin importar el
      // largo del ZIP.
      if (!distanceValid) {
        toast.error(t("Please verify your address is within our service area.",
                      "Verifica que tu dirección esté dentro de nuestra área de servicio."));
        return false;
      }
    }

    if (cur === 2) {
      if (!form.service_type)  return err(t("Select a service",         "Selecciona un servicio"));
      if (!form.service_plan)  return err(t("Select a turnaround plan", "Selecciona un plan de tiempo"));
      if (form.recurrence === "twice_week" && (!form.recurrence_days || form.recurrence_days.length !== 2)) {
        return err(t("Please select exactly two days for twice-a-week pickup",
                     "Por favor selecciona exactamente dos días para recogidas dos veces por semana"));
      }
    }

    if (cur === 3) {
      if (!form.pickup_date) return err(t("Select a pickup date", "Selecciona una fecha de recogida"));
      const dateCheck = validatePickupDateValue(form.pickup_date);
      if (!dateCheck.ok) {
        if (dateCheck.reason === "past")
          return err(t("Pickup date cannot be in the past. Select a date from today onwards.",
                        "La fecha de recogida no puede ser pasada. Selecciona una fecha desde hoy en adelante."));
        if (dateCheck.reason === "year")
          return err(t("Enter a valid year for the pickup date.",
                        "Ingresa un año válido para la fecha de recogida."));
        if (dateCheck.reason === "future")
          return err(t("Pickup date is too far in the future.",
                        "La fecha de recogida está muy adelantada en el futuro."));
        return err(t("Enter a valid date (YYYY-MM-DD).",
                      "Ingresa una fecha válida (AAAA-MM-DD)."));
      }
      if (!form.pickup_time) return err(t("Select a time window", "Selecciona un horario"));
      const avail = getTimeSlotAvailability(form.pickup_date, form.service_plan, todayStr);
      if (form.pickup_time === "8am-12pm" && !avail.morning) {
        return err(t("Morning window no longer available.", "La ventana de mañana ya no está disponible."));
      }
      if (form.pickup_time === "2pm-6pm" && !avail.afternoon) {
        return err(t("Afternoon window no longer available today.", "La ventana de tarde ya no está disponible hoy."));
      }
    }

    if (cur === 4 && !form.terms)
      return err(t("Accept terms to continue", "Acepta los términos"));

    return true;
  };

  const handleNext = async () => {
    if (!validate()) return;
    if (cur === 1) {
      const fullAddress = [form.address_line1, form.city, form.state, form.zip_code]
        .filter(Boolean).join(", ");
      const isOk = await checkDistance(fullAddress);
      if (!isOk) return;
    }
    if (cur < 4) { goTo(cur + 1); return; }

    setWashPhase(0); setWashDone(false);
    const phaseDurs = [900, 1100, 1000, 900, 700];
    let cum = 0;
    phaseDurs.forEach((d, i) => { setTimeout(() => setWashPhase(i), cum); cum += d; });

    setSubmitting(true);
    try {
      const fullPhone   = `${form.dialCode} ${form.phone}`.trim();
      const fullAddress = [form.address_line1, form.address_line2, form.city, form.state, form.zip_code].filter(Boolean).join(", ");
      const addonList   = Array.from(selectedAddons.values());
      const addonNote   = addonList.length > 0
        ? `\nAdd-on services: ${addonList.map(s => `${s.name}${s.price != null ? ` ($${s.price}${s.price_unit ? "/" + s.price_unit.replace("per_", "") : ""})` : ""}`).join(", ")}`
        : "";
      const notes = [
        form.wash_temp  ? `Wash temp: ${form.wash_temp}`  : "",
        form.dry_temp   ? `Dry temp: ${form.dry_temp}`    : "",
        form.notes?.trim(),
        form.addr_notes ? `Pickup note: ${form.addr_notes}` : "",
        `Contact via: ${form.contact_method}`,
        form.service_plan ? `Service plan: ${form.service_plan}` : "",
        addonNote,
      ].filter(Boolean).join("\n");

      let recurrencePayload = { recurrence: "once", recurrence_end_date: null, recurrence_days: null };
      if (form.recurrence && form.recurrence !== "once") {
        recurrencePayload = {
          recurrence:          form.recurrence,
          recurrence_end_date: form.recurrence_end_date || null,
          recurrence_days:     form.recurrence === "twice_week" ? form.recurrence_days : null,
        };
      }

      const captcha_token = await getRecaptchaToken("pickup_request");
      const res = await axios.post(`${API}/public/pickup-request`, {
        name:           `${form.first_name} ${form.last_name}`.trim(),
        email:          form.email.trim(),
        phone:          fullPhone,
        address:        fullAddress,
        pickup_date:    form.pickup_date,
        pickup_time:    form.pickup_time,
        service_type:   form.service_type,
        service_plan:   form.service_plan,
        contact_method: form.contact_method,
        sms_consent:    form.sms_consent,
        notes,
        addon_services: addonList.map(s => ({ id: s.id, name: s.name, price: s.price, price_unit: s.price_unit, category: s.category })),
        // FIX: distance_miles y delivery_fee se calculaban correctamente en
        // el paso 2 (vía /store/check-address, con geocodificación real) y
        // se mostraban en el resumen de confirmación — pero nunca se
        // incluían en el payload que crea la orden. Esto hacía que el
        // backend guardara la orden sin distance_miles, y por lo tanto
        // calculate_delivery_fee() siempre recibía None y cobraba $0 de
        // envío sin importar la distancia real. Ahora se mandan ambos
        // valores ya calculados para que el backend los guarde en la orden.
        distance_miles: form.distance_miles,
        delivery_fee:   form.delivery_fee,
        captcha_token,
        ...recurrencePayload,
      });
      setPendingVerification(res.data);
    } catch (e) { toast.error(getErr(e)); setSubmitFailed(true); }
    finally { setSubmitting(false); }

    setTimeout(() => {
      setWashPhase(5); setWashDone(true);
      clearSavedSession();
    }, cum + 400);
  };

  const [verifiedResult, setVerifiedResult] = useState(null);

  const handleVerifyCode = async () => {
    if (!pendingVerification?.temp_token || !verificationCode.trim()) {
      toast.error(t("Enter the code we sent you", "Ingresa el código que te enviamos"));
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/public/verify-code`, {
        temp_token: pendingVerification.temp_token,
        code: verificationCode.trim(),
      });
      setVerifiedResult(res.data);
      setPendingVerification(null);
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingVerification?.temp_token) return;
    try {
      await axios.post(`${API}/public/resend-code`, { temp_token: pendingVerification.temp_token });
      toast.success(t("Code resent", "Código reenviado"));
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  const handleReset = () => {
    setForm({ ...EMPTY });
    setCur(0);
    setFormKey(k => k + 1);
    setWashPhase(-1); setWashDone(false);
    setPendingVerification(null); setVerificationCode(""); setVerifiedResult(null); setSubmitFailed(false);
    setShowResumeBanner(false);
    setSelectedAddons(new Map());
    clearAddressValidation();
    scrollToForm();
  };

  const g2   = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
  const g3   = { display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8 };
  const fGap = { display: "flex", flexDirection: "column", gap: 14 };

  const svcMap  = { pickup_delivery: t("Pickup & Delivery", "Recogida y Entrega"), airbnb_host: t("Airbnb Host", "Anfitrión Airbnb"), commercial: t("Commercial / B2B", "Comercial / B2B") };
  const planMap = { standard: t("Standard (36h)", "Estándar (36h)"), premium: t("Premium (24h)", "Premium (24h)"), express: t("Express (Same Day)", "Express (mismo día)") };
  const tempMap = { cold: "Cold ≤30°C", warm: "Warm 40°C", hot: "Hot 60°C+", any: t("Any temperature", "Cualquier temperatura") };
  const dryMap  = { low: t("Low heat", "Calor bajo"), medium: t("Medium heat", "Calor medio"), high: t("High heat", "Calor alto"), air: t("Air dry", "Secado al aire") };
  const timeMap = { "8am-12pm": "8:00 AM – 12:00 PM", "2pm-6pm": "2:00 PM – 6:00 PM" };
  const cmMap   = { phone: t("Phone call", "Llamada"), text: "Text/SMS", email: "Email" };

  const selectedAddonsList = Array.from(selectedAddons.values());
  const selectedAddonIds   = new Set(selectedAddons.keys());
  const recurrenceLabel    = form.recurrence && form.recurrence !== "once"
    ? (locale === "es" ? RECURRENCE_LABELS[form.recurrence]?.es : RECURRENCE_LABELS[form.recurrence]?.en)
    : null;
  const twiceWeekDays = (form.recurrence === "twice_week" && form.recurrence_days.length === 2)
    ? form.recurrence_days.map(d => {
        const found = WEEKDAYS.find(w => w.value === d);
        return locale === "es" ? found?.label.es : found?.label.en;
      }).join(" / ")
    : null;

  const todayAvailability = getTimeSlotAvailability(todayStr, form.service_plan, todayStr);

  return (
    <div className="min-h-screen" style={{ background: "hsl(var(--background))" }}>
      <PublicNav />

      <style>{`
        @keyframes tl_spin   { to { transform: rotate(360deg)  } }
        @keyframes tl_spinr  { to { transform: rotate(-360deg) } }
        @keyframes tl_pulse  { 0%{transform:scale(.85);opacity:.8} 100%{transform:scale(1.35);opacity:0} }
        @keyframes tl_float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes tl_panel  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes tl_wash   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes tl_shimmer{ 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }
        .scrollbar-hide::-webkit-scrollbar{display:none}
        .scrollbar-hide{scrollbar-width:none}
        /* FIX: truco de detección de autofill — dispara una animación de
           0ms apenas Chrome/Edge/Safari marcan el campo como autocompletado,
           así podemos leer el valor real del DOM y sincronizarlo con React
           (ver handleAutofillDetected en PhoneInput). */
        @keyframes onAutoFillStart { from {} to {} }
        .tl-phone-autofill-input:-webkit-autofill {
          animation-name: onAutoFillStart;
          animation-duration: 0.001s;
        }
      `}</style>

      <section ref={topRef} style={{ paddingTop: 80, paddingBottom: 0,
        background: "linear-gradient(150deg,#0b1929 0%,#081320 55%,#040c16 100%)",
        position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.022) 1px,transparent 1px)", backgroundSize: "44px 44px", pointerEvents: "none" }} />
        <div style={{ width: "100%", maxHeight: 280, overflow: "hidden", position: "relative" }}>
          <img src={heroBanner} alt="Ventura Fresh Laundry"
            style={{ width: "100%", height: 280, objectFit: "cover", objectPosition: "center", display: "block", opacity: 0.75 }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 120, background: "linear-gradient(to bottom, transparent, #081320)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 60, background: "linear-gradient(to bottom, #0b1929, transparent)", pointerEvents: "none" }} />
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 24px 40px", position: "relative", zIndex: 2 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 20, padding: "4px 12px", marginBottom: 14 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399", boxShadow: "0 0 6px rgba(52,211,153,.9)", display: "inline-block" }} />
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".16em", color: "rgba(255,255,255,.5)" }}>
              {t("Premium Laundry Service", "Servicio Premium de Lavandería")}
            </span>
          </div>
          <h1 style={{ fontFamily: "'Manrope',sans-serif", fontSize: "clamp(24px,4vw,42px)", fontWeight: 800, color: "white", lineHeight: 1.15, letterSpacing: "-.02em", margin: "0 0 10px" }}>
            {t("Your Pick-up", "Tu Recogida")}<br />
            <span style={{ color: "#38bdf8" }}>{t("Begins Here.", "Comienza Aquí.")}</span>
          </h1>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,.45)", lineHeight: 1.7, maxWidth: 380, margin: 0 }}>
            {t("Follow the wash cycle — each stage is a step on the conveyor belt.",
               "Sigue el ciclo de lavado — cada etapa es un paso en la cinta transportadora.")}
          </p>
        </div>
      </section>

      <section style={{ padding: "0 0 64px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 16px" }}>
          <div ref={formRef} id="schedule-pickup-form" style={{ marginTop: "-20px", position: "relative", zIndex: 2 }}>
            <ConveyorTrack cur={washPhase >= 0 ? 4 : cur} locale={locale} onStageClick={(i) => { if (washPhase < 0) goTo(i); }} />
          </div>

          {showResumeBanner && washPhase < 0 && (
            <div style={{ marginTop: 12, padding: "11px 16px", borderRadius: 12,
              background: "linear-gradient(135deg,rgba(14,165,233,.08),rgba(56,189,248,.04))",
              border: "1px solid rgba(14,165,233,.3)",
              display: "flex", alignItems: "center", gap: 10, animation: "tl_panel .3s ease both" }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>💾</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0ea5e9" }}>{t("You have a saved session", "Tienes una sesión guardada")}</div>
                <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                  {t(`Resuming from step ${cur + 1}`, `Retomando desde el paso ${cur + 1}`)}
                  {form.first_name ? ` · ${locale === "es" ? "¡Hola" : "Hi"}, ${form.first_name}!` : ""}
                </div>
              </div>
              <button type="button" onClick={() => setShowResumeBanner(false)}
                style={{ fontSize: 11, fontWeight: 700, color: "#0ea5e9", background: "none", border: "none", cursor: "pointer", padding: "4px 8px", fontFamily: "inherit" }}>
                {t("Continue →", "Continuar →")}
              </button>
              <button type="button" onClick={handleReset}
                style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", background: "none", border: "none", cursor: "pointer", padding: "4px 8px", fontFamily: "inherit" }}>
                {t("Start over", "Empezar de nuevo")}
              </button>
            </div>
          )}

          {washPhase >= 0 && (
            <div style={{ background: "#07111d", borderRadius: 16, marginTop: 16, border: "0.5px solid rgba(14,165,233,.2)", padding: "32px 24px", textAlign: "center", animation: "tl_wash .4s ease both" }}>
              {!washDone ? (
                <>
                  <div style={{ width: 180, margin: "0 auto", animation: "tl_float 3s ease-in-out infinite" }}><WashMachine phase={washPhase} done={false} /></div>
                  <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Manrope',sans-serif", color: "white", marginTop: 14 }}>
                    {[t("Pre-rinsing…","Pre-enjuague…"), t("Washing…","Lavando…"), t("Rinsing…","Enjuagando…"), t("Spinning…","Centrifugando…"), t("Done!","¡Listo!")][Math.min(washPhase, 4)]}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16 }}>
                    {WASH_PHASES.map((p, i) => (
                      <div key={i} style={{ textAlign: "center", width: 52 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", margin: "0 auto 4px",
                          background: i < washPhase ? "#0ea5e9" : i === washPhase ? "#38bdf8" : "rgba(255,255,255,.1)",
                          boxShadow: i === washPhase ? "0 0 0 3px rgba(56,189,248,.25)" : "none", transition: "all .3s" }} />
                        <div style={{ fontSize: 9, fontWeight: 600, color: i <= washPhase ? "rgba(255,255,255,.6)" : "rgba(255,255,255,.2)" }}>{p}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : submitFailed ? (
                <div style={{ animation: "tl_panel .5s ease both" }}>
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(239,68,68,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "12px auto 10px" }}>⚠️</div>
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Manrope',sans-serif", color: "white", marginBottom: 10 }}>
                    {t("Something went wrong", "Algo salió mal")}
                  </div>
                  <button onClick={() => { setSubmitFailed(false); setWashPhase(-1); }} style={{ padding: "11px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    {t("Try again", "Intentar de nuevo")}
                  </button>
                </div>
              ) : pendingVerification ? (
                <div style={{ animation: "tl_panel .5s ease both" }}>
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(56,189,248,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "12px auto 10px" }}>📩</div>
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Manrope',sans-serif", color: "white", marginBottom: 6 }}>
                    {t("Enter your verification code", "Ingresa tu código de verificación")}
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,.5)", maxWidth: 280, margin: "0 auto 16px", lineHeight: 1.6 }}>
                    {pendingVerification.message}
                  </p>
                  <input
                    type="text" inputMode="numeric" maxLength={6} value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    style={{ width: 160, textAlign: "center", letterSpacing: 6, fontSize: 22, fontWeight: 800, padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.2)", background: "rgba(255,255,255,.05)", color: "white", margin: "0 auto 14px", display: "block" }}
                  />
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button onClick={handleVerifyCode} disabled={verifying} style={{ padding: "11px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: verifying ? 0.6 : 1 }}>
                      {verifying ? t("Verifying…", "Verificando…") : t("Confirm", "Confirmar")}
                    </button>
                  </div>
                  <button onClick={handleResendCode} style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,.4)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                    {t("Didn't get it? Resend code", "¿No te llegó? Reenviar código")}
                  </button>
                </div>
              ) : (
                <div style={{ animation: "tl_panel .5s ease both" }}>
                  <div style={{ width: 180, margin: "0 auto", animation: "tl_float 4s ease-in-out infinite" }}><WashMachine phase={5} done={true} /></div>
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(52,211,153,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "12px auto 10px" }}>🎉</div>
                  <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Manrope',sans-serif", color: "white", marginBottom: 6 }}>
                    {t("Request submitted!", "¡Solicitud enviada!")}
                  </div>
                  {form.recurrence && form.recurrence !== "once" && (
                    <div style={{ margin: "0 auto 16px", maxWidth: 320, padding: "10px 14px", borderRadius: 10,
                      background: "rgba(14,165,233,.1)", border: "1px solid rgba(14,165,233,.2)", animation: "tl_panel .4s ease both" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#38bdf8", marginBottom: 3 }}>
                        🔄 {t("Recurring schedule confirmed", "Programación recurrente confirmada")}
                      </div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,.55)" }}>
                        {form.recurrence === "twice_week" && twiceWeekDays ? `${recurrenceLabel} (${twiceWeekDays})` : recurrenceLabel}
                        {form.recurrence_end_date ? ` · ${t("Until", "Hasta")} ${form.recurrence_end_date}` : ` · ${t("Indefinite", "Indefinido")}`}
                      </div>
                    </div>
                  )}
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,.45)", maxWidth: 280, lineHeight: 1.65, margin: "0 auto 20px" }}>
                    {t("Our team will confirm your pickup via", "Nuestro equipo confirmará por")}{" "}
                    <strong style={{ color: "#38bdf8" }}>{cmMap[form.contact_method] || t("your preferred method", "tu método preferido")}</strong>.
                  </p>
                  <button onClick={handleReset} style={{ padding: "11px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    🔄 {t("Schedule another", "Programar otra")}
                  </button>
                </div>
              )}
            </div>
          )}

          {washPhase < 0 && (
            <div key={formKey} style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 16, boxShadow: "var(--shadow-lg)", overflow: "hidden", marginTop: 16, animation: "tl_panel .3s ease both" }}>
              <div style={{ height: 3, background: "linear-gradient(90deg,#38bdf8,#0ea5e9,#2563eb)" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 20px", borderBottom: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(14,165,233,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{STAGES[cur].icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'Manrope',sans-serif", color: "hsl(var(--foreground))" }}>
                    {locale === "es" ? STAGES[cur].subES : STAGES[cur].subEN}
                  </div>
                  <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 1 }}>
                    {t(`Step ${cur + 1} of 5`, `Paso ${cur + 1} de 5`)} — {locale === "es" ? STAGES[cur].es : STAGES[cur].en}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  {STAGES.map((_, i) => (
                    <div key={i} style={{ width: i === cur ? 20 : 7, height: 7, borderRadius: 3.5,
                      background: i < cur ? "#0ea5e9" : i === cur ? "#0ea5e9" : "hsl(var(--border))",
                      transition: "all .3s", cursor: i < cur ? "pointer" : "default" }}
                      onClick={() => i < cur && goTo(i)} />
                  ))}
                </div>
              </div>

              <div style={{ padding: "20px 24px" }}>
                <div style={fGap}>

                  {cur === 0 && (
                    <>
                      <div style={g2}>
                        <FF label={t("First name *", "Nombre *")}><FInput value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} placeholder="John" autoComplete="given-name" /></FF>
                        <FF label={t("Last name *", "Apellido *")}><FInput value={form.last_name} onChange={(e) => setF("last_name", e.target.value)} placeholder="Smith" autoComplete="family-name" /></FF>
                      </div>
                      <FF label={t("Email *", "Correo *")}><FInput type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} placeholder="you@example.com" autoComplete="email" /></FF>
                      <FF label={t("Phone *", "Teléfono *")}>
                        <PhoneInput value={form.phone} dialCode={form.dialCode} dialIso={form.dialIso}
                          onValueChange={(v) => setF("phone", v)}
                          onDialCodeChange={(code, iso) => { setF("dialCode", code); setF("dialIso", iso); setF("phone", ""); }} />
                      </FF>
                      <FF label={t("Best way to reach you *", "Cómo contactarte *")}>
                        <ChipSet value={form.contact_method}
                          onChange={(v) => { setF("contact_method", v); if (v !== "text") setF("sms_consent", false); }}
                          options={[
                            { val: "phone", icon: "📞", label: t("Phone call", "Llamada") },
                            { val: "text",  icon: "💬", label: "Text/SMS" },
                            { val: "email", icon: "✉️", label: "Email" },
                          ]} />
                      </FF>
                      {form.contact_method === "text" && (
                        <SmsConsentField checked={form.sms_consent} onChange={(e) => setF("sms_consent", e.target.checked)} idPrefix="pickup-sms" />
                      )}
                    </>
                  )}

                  {cur === 1 && (
                    <>
                      <FF label={t("Street address *", "Dirección *")}>
                        <AddressAutocomplete
                          value={form.address_line1}
                          onChange={(v) => { setF("address_line1", v); clearAddressValidation(); }}
                          onSelect={(addr) => {
                            // FIX: `addr.full` ahora sí existe (viene de la
                            // corrección en AddressAutocomplete.js) y contiene
                            // SOLO la calle, sin ciudad/estado/CP repetidos.
                            const fullStreet = addr.full || addr.formatted ||
                              (addr.street_number && addr.route ? `${addr.street_number} ${addr.route}` : addr.street);
                            setF("address_line1", fullStreet);
                            if (addr.city)  setF("city", addr.city);
                            if (addr.state) setF("state", addr.state.length > 2 ? addr.state.substring(0, 2).toUpperCase() : addr.state.toUpperCase());
                            if (addr.zip)   setF("zip_code", addr.zip);
                            // Nueva selección de dirección → invalidar cualquier
                            // verificación previa; se re-verificará contra la
                            // dirección nueva en el useEffect de checkDistance.
                            clearAddressValidation();
                          }}
                          placeholder={t("123 Main St", "Calle Principal 123")}
                          renderInput={(props) => <FInput {...props} data-testid="pickup-address-autocomplete" />}
                        />
                      </FF>
                      <FF label={t("Apt / Suite (optional)", "Apto / Suite (opcional)")}><FInput value={form.address_line2} onChange={(e) => setF("address_line2", e.target.value)} placeholder={t("Apt 4B…", "Apto 4B…")} /></FF>
                      <div style={g3}>
                        <FF label={t("City *", "Ciudad *")}><FInput value={form.city} onChange={(e) => { setF("city", e.target.value); clearAddressValidation(); }} placeholder="Los Angeles" autoComplete="address-level2" /></FF>
                        <FF label={t("State *", "Estado *")}><FInput value={form.state} onChange={(e) => { setF("state", e.target.value.toUpperCase()); clearAddressValidation(); }} placeholder="CA" maxLength={2} /></FF>
                        <FF label={t("ZIP *", "CP *")}>
                          <FInput value={form.zip_code} onChange={(e) => { setF("zip_code", e.target.value); clearAddressValidation(); }} placeholder="90001" maxLength={10} />
                        </FF>
                      </div>

                      <AddressValidationStatus
                        checking={distanceChecking}
                        valid={distanceValid}
                        error={distanceError}
                        distanceMiles={distanceMiles || form.distance_miles}
                        deliveryFee={deliveryFee !== null ? deliveryFee : form.delivery_fee}
                        locale={locale}
                        t={t}
                      />

                      <FF label={t("Access notes (optional)", "Notas de acceso (opcional)")}>
                        <FTextarea value={form.addr_notes} onChange={(e) => setF("addr_notes", e.target.value)} placeholder={t("Gate code, building entrance…", "Código de puerta, entrada del edificio…")} rows={2} />
                      </FF>
                    </>
                  )}

                  {cur === 2 && (
                    <>
                      <FF label={t("Service type *", "Tipo de servicio *")}>
                        <OptionCards value={form.service_type} onChange={(v) => setF("service_type", v)}
                          options={[
                            { val: "pickup_delivery", icon: "🚚", title: t("Pickup & Delivery", "Recogida y Entrega"), desc: t("We wash, fold & return", "Lavamos, doblamos y entregamos") },
                            { val: "airbnb_host",     icon: "🏠", title: t("Airbnb Host", "Anfitrión Airbnb"),         desc: t("Linens, towels & priority", "Sábanas, toallas y prioridad"), badge: t("NEW", "NUEVO"), badgeBg: "rgba(255,92,37,.12)", badgeColor: "#ff5c25", badgeBorder: "rgba(255,92,37,.3)", accentColor: "#ff5c25", accentBg: "rgba(255,92,37,.07)", accentGlow: "rgba(255,92,37,.18)" },
                            { val: "commercial",      icon: "🏢", title: t("Commercial / B2B", "Comercial / B2B"),     desc: t("Bulk & business laundry", "Lavado masivo y empresas") },
                          ]} />

                        {form.service_type === "pickup_delivery" && (
                          <>
                            <FF label={t("Turnaround plan *", "Plan de tiempo *")}>
                              <PlanSelector value={form.service_plan} onChange={(v) => setF("service_plan", v)} serviceType={form.service_type} membershipPlan={membershipInfo?.plan} />
                            </FF>
                            <PickupDeliveryInfoPanel t={t} />
                          </>
                        )}
                        {form.service_type === "airbnb_host" && (
                          <>
                            <FF label={t("Turnaround plan *", "Plan de tiempo *")}>
                              <PlanSelector value={form.service_plan} onChange={(v) => setF("service_plan", v)} serviceType={form.service_type} membershipPlan={membershipInfo?.plan} />
                            </FF>
                            <AirbnbInfoPanel t={t} />
                          </>
                        )}
                        {form.service_type === "commercial" && (
                          <>
                            <FF label={t("Turnaround plan *", "Plan de tiempo *")}>
                              <PlanSelector value={form.service_plan} onChange={(v) => setF("service_plan", v)} serviceType={form.service_type} membershipPlan={membershipInfo?.plan} />
                            </FF>
                            <CommercialInfoPanel t={t} />
                          </>
                        )}

                        <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 8 }}>
                          {t("Need Wash & Fold drop-off?", "¿Necesitas Wash & Fold drop-off?")}{" "}
                          <Link to="/wash-fold" style={{ color: "#0ea5e9", fontWeight: 600 }}>{t("Go to form →", "Ir al formulario →")}</Link>
                        </p>
                      </FF>

                      {showRecurrenceSelector && (
                        <FF label={t("Pickup frequency", "Frecuencia de recogida")}>
                          <RecurrenceSelector
                            value={form.recurrence} onChange={(v) => setF("recurrence", v)}
                            endDate={form.recurrence_end_date} onEndDateChange={(v) => setF("recurrence_end_date", v)}
                            selectedDays={form.recurrence_days} onDaysChange={(days) => setF("recurrence_days", days)}
                            locale={locale} t={t}
                          />
                        </FF>
                      )}

                      <FF label={t("Wash temperature", "Temperatura de lavado")}>
                        <TempRow value={form.wash_temp} onChange={(v) => setF("wash_temp", v)} options={WASH_TEMP_OPTIONS} />
                      </FF>
                      <FF label={t("Dry temperature", "Temperatura de secado")}>
                        <TempRow value={form.dry_temp} onChange={(v) => setF("dry_temp", v)} options={DRY_TEMP_OPTIONS} />
                      </FF>
                      <FF label={t("Special instructions (optional)", "Instrucciones especiales (opcional)")}>
                        <FTextarea value={form.notes} onChange={(e) => setF("notes", e.target.value)}
                          placeholder={t("Detergent type, hang-dry items, folding style…", "Tipo de detergente, prendas a secar, estilo de doblado…")} />
                      </FF>
                      {addonServices.length > 0 && (
                        <FF label={t("Add-on services (optional)", "Servicios adicionales (opcional)")}>
                          <AddonServicesSection services={addonServices} selectedIds={selectedAddonIds} onToggle={toggleAddon} t={t} locale={locale} />
                        </FF>
                      )}
                    </>
                  )}

                  {cur === 3 && (
                    <>
                      <FF label={t("Preferred pickup date", "Fecha preferida")}>
                        <FInput
                          type="date"
                          value={form.pickup_date}
                          onChange={(e) => handlePickupDateChange(e.target.value)}
                          min={getMinPickupDate(form.service_plan)}
                          max={getMaxPickupDate()}
                          style={{ cursor: "pointer" }}
                        />
                        {form.service_plan === "express" ? (
                          todayAvailability.noSlots ? (
                            <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4, paddingLeft: 4 }}>
                              ⏰ {locale === "es" ? "Sin ventanas disponibles hoy — selecciona mañana." : "No windows today — select tomorrow or later."}
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: "#0ea5e9", marginTop: 4, paddingLeft: 4 }}>
                              ✨ {locale === "es" ? "Express: mismo día disponible." : "Express: same-day available."}
                            </div>
                          )
                        ) : (
                          <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4, paddingLeft: 4 }}>
                            ⏰ {locale === "es" ? "Planes Standard y Premium: fecha más próxima es mañana." : "Standard & Premium: earliest pickup is tomorrow."}
                          </div>
                        )}
                        {form.recurrence && form.recurrence !== "once" && (
                          <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 7,
                            background: "rgba(14,165,233,.07)", border: "1px solid rgba(14,165,233,.18)",
                            fontSize: 10, color: "#0ea5e9", display: "flex", alignItems: "center", gap: 5 }}>
                            🔄 {t("This is your first pickup date.", "Esta es tu primera fecha.")}
                            {form.recurrence === "twice_week" && twiceWeekDays && ` (${twiceWeekDays})`}
                          </div>
                        )}
                      </FF>
                      <FF label={t("Preferred time window *", "Horario preferido *")}>
                        <TimeSlots
                          value={form.pickup_time} onChange={(v) => setF("pickup_time", v)}
                          locale={locale} pickupDate={form.pickup_date}
                          servicePlan={form.service_plan} todayStr={todayStr}
                        />
                        <p style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", marginTop: 5 }}>
                          {t("Pacific Time · Our team will confirm via your preferred method",
                             "Hora del Pacífico · Confirmamos por tu método preferido")}
                        </p>
                      </FF>
                    </>
                  )}

                  {cur === 4 && (
                    <>
                      <SumBlock title={`👤 ${t("Contact", "Contacto")}`} rows={[
                        [t("Name","Nombre"),    `${form.first_name} ${form.last_name}`.trim()],
                        [t("Email","Correo"),    form.email],
                        [t("Phone","Teléfono"), `${form.dialCode} ${form.phone}`.trim()],
                        [t("Contact via","Via"), cmMap[form.contact_method]],
                      ]} />
                      <SumBlock title={`📍 ${t("Address", "Dirección")}`} rows={[
                        [t("Street","Calle"),  form.address_line1],
                        [t("City / State / ZIP","Ciudad / Estado / CP"), [form.city, form.state, form.zip_code].filter(Boolean).join(", ")],
                        ...(distanceMiles || form.distance_miles ? [[
                          t("Delivery","Entrega"),
                          (() => {
                            const miles = distanceMiles || form.distance_miles;
                            const fee = deliveryFee !== null ? deliveryFee : form.delivery_fee;
                            if (fee === null || fee === undefined) return `${miles.toFixed(1)} mi — ${t("Calculating…", "Calculando…")}`;
                            const formattedFee = fee === 0 ? t("FREE", "GRATIS") : `$${fee.toFixed(2)}`;
                            return `${miles.toFixed(1)} mi — ${formattedFee}`;
                          })()
                        ]] : []),
                      ]} />
                      <SumBlock title={`🧺 ${t("Service", "Servicio")}`} rows={[
                        [t("Type","Tipo"),    svcMap[form.service_type]],
                        ...(form.service_plan ? [[t("Plan","Plan"), planMap[form.service_plan]]] : []),
                        ...(membershipInfo?.plan ? [[t("Pricing","Precio"), `${getMembershipDiscount(membershipInfo.plan)?.badge || "⭐"} ${t("Member rate","Tarifa miembro")}`]] : []),
                        ...(recurrenceLabel ? [[t("Frequency","Frecuencia"), `🔄 ${recurrenceLabel}${form.recurrence === "twice_week" && twiceWeekDays ? ` (${twiceWeekDays})` : ""}`]] : []),
                        [t("Wash temp","Temp lavado"), tempMap[form.wash_temp]],
                        [t("Dry temp","Temp secado"),  dryMap[form.dry_temp]],
                        ...(form.notes ? [[t("Notes","Notas"), form.notes.slice(0,70)]] : []),
                      ]} />

                      {selectedAddonsList.length > 0 && (
                        <div style={{ padding: "10px 12px", borderRadius: 9, background: "rgba(14,165,233,.05)", border: "1px solid rgba(14,165,233,.2)" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".12em", color: "#0ea5e9", marginBottom: 8 }}>
                            ✨ {t("Add-on Services", "Servicios Adicionales")}
                          </div>
                          {selectedAddonsList.map((s) => (
                            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "3px 0", fontSize: 11 }}>
                              <span style={{ color: "hsl(var(--foreground))" }}>{getServiceIcon(s.name, s.category || "")} {s.name}</span>
                              {s.price != null && (
                                <span style={{ fontWeight: 700, color: "#0ea5e9", flexShrink: 0 }}>
                                  ${s.price}
                                  {s.price_unit && <span style={{ fontSize: 9, fontWeight: 500, color: "hsl(var(--muted-foreground))", marginLeft: 2 }}>{(PRICE_UNIT_LABELS[s.price_unit] || {}).en || ""}</span>}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <SumBlock title={`📅 ${t("Schedule", "Horario")}`} rows={[
                        [t("Date","Fecha"),    form.pickup_date || t("Flexible","Flexible")],
                        [t("Window","Ventana"), timeMap[form.pickup_time]],
                      ]} />
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: "10px 12px", background: "hsl(var(--secondary))", borderRadius: 9, border: "0.5px solid hsl(var(--border))" }}>
                        <input type="checkbox" id="sp-terms" checked={form.terms} onChange={(e) => setF("terms", e.target.checked)}
                          style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1, accentColor: "#0ea5e9", cursor: "pointer" }} />
                        <label htmlFor="sp-terms" style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", lineHeight: 1.5, cursor: "pointer" }}>
                          {t("I accept the", "Acepto los")}{" "}
                          <Link to="/terms-and-conditions" style={{ color: "#0ea5e9", fontWeight: 600 }}>{t("Terms", "Términos")}</Link>{" & "}
                          <Link to="/privacy-policy" style={{ color: "#0ea5e9", fontWeight: 600 }}>{t("Privacy Policy", "Privacidad")}</Link>.{" "}
                          {t("By submitting I authorize Ventura Fresh Laundry to contact me.",
                             "Al enviar autorizo a Ventura Fresh Laundry a contactarme.")}
                        </label>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                  {cur > 0 && (
                    <button type="button" onClick={() => goTo(cur - 1)}
                      style={{ padding: "10px 16px", borderRadius: 9, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--muted-foreground))", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
                      ← {t("Back", "Atrás")}
                    </button>
                  )}
                  <button type="button" onClick={handleNext}
                    disabled={
                      submitting ||
                      (cur === 4 && !form.terms) ||
                      (cur === 1 && !!distanceError) ||
                      (cur === 1 && distanceChecking) ||
                      (cur === 2 && form.recurrence === "twice_week" && form.recurrence_days.length !== 2) ||
                      (cur === 3 && !form.pickup_date)
                    }
                    style={{ flex: 1, padding: "11px 16px", borderRadius: 9, border: "none",
                      background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "white",
                      fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em",
                      cursor: (submitting || (cur === 4 && !form.terms) || (cur === 1 && !!distanceError) || (cur === 1 && distanceChecking) || (cur === 2 && form.recurrence === "twice_week" && form.recurrence_days.length !== 2) || (cur === 3 && !form.pickup_date))
                        ? "not-allowed" : "pointer",
                      opacity: (submitting || (cur === 4 && !form.terms) || (cur === 1 && !!distanceError) || (cur === 1 && distanceChecking) || (cur === 2 && form.recurrence === "twice_week" && form.recurrence_days.length !== 2) || (cur === 3 && !form.pickup_date))
                        ? 0.5 : 1,
                      fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                      boxShadow: "var(--shadow-sky)", transition: "all .15s", position: "relative", overflow: "hidden" }}>
                    {submitting ? (
                      <>
                        <div style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "white", borderRadius: "50%", animation: "tl_spin .7s linear infinite" }} />
                        {t("Sending…", "Enviando…")}
                      </>
                    ) : cur === 1 && distanceChecking ? (
                      <>
                        <div style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "white", borderRadius: "50%", animation: "tl_spin .7s linear infinite" }} />
                        {t("Verifying address…", "Verificando dirección…")}
                      </>
                    ) : cur < 4 ? (
                      <>{t("Next", "Siguiente")}: {locale === "es" ? STAGES[cur + 1].es : STAGES[cur + 1].en} →</>
                    ) : (
                      <>🚀 {t("Start the wash cycle!", "¡Iniciar ciclo de lavado!")}</>
                    )}
                    <span style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg,transparent,rgba(255,255,255,.1),transparent)", transform: "translateX(-100%)", animation: "tl_shimmer 2s ease infinite" }} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}